import React, { useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete } from "@react-google-maps/api";
import { Helmet } from "react-helmet";
import api from "../utils/api";
import { AJP_MODE_STYLES, getAjpConversionLinks } from "../config/ajpConversionLinks";
import JourneyAlertConversion from "../components/ajp/JourneyAlertConversion";
import AjpRouteSeoContent from "../components/ajp/AjpRouteSeoContent";
import { readAjpPlanSession, restoredPlanPayload, saveAjpPlanSession } from "../utils/ajpPlanSession";
import { getFunnelJourneyId, trackAjpPlanGoogleAdsConversion, trackFunnelEvent, trackFunnelEventOnce } from "../utils/funnelTracking";
import "./Homenewajpv2.css";

const airports = {
  heathrow: {
    key: "heathrow",
    name: "Heathrow",
    code: "LHR",
    path: "/heathrow",
    accent: "#1d4ed8",
    summary: "Live road, rail, terminal and flight-aware planning for Heathrow journeys.",
    status: {
      road: "Good",
      rail: "Good",
      flights: "Moderate",
      terminals: "Good",
      queues: "Good",
      weather: "11C • Light cloud",
      overall: "Good",
    },
    preview: {
      arrivals: "469",
      departures: "236",
      terminal: "T2 Good • T3 Busy • T4 Good • T5 Good",
      weather: "11C • Light cloud",
      flights: [
        "BA123 New York (JFK) • On time",
        "VS43 Atlanta (ATL) • On time",
        "LH247 Frankfurt (FRA) • Delayed 20m",
      ],
      queues: [
        "Security T2 • 0-5 min",
        "Security T3 • 10-15 min",
        "Border Control T2 • 5-10 min",
      ],
      road: [
        "M25 (J15-J16) • Good",
        "M4 (J4-J4b) • Good",
        "A4 Bath Road • Moderate",
      ],
      rail: [
        "Heathrow Express • On time",
        "Elizabeth line • Good service",
        "South Western Railway • Good service",
      ],
    },
  },
  ema: {
    key: "ema",
    name: "East Midlands Airport",
    code: "EMA",
    path: "/east-midlands-airport",
    accent: "#0f766e",
    summary: "Deterministic planning for EMA with road corridor, rail gateway and flight checks.",
    status: {
      road: "Good",
      rail: "Good",
      flights: "On time",
      terminals: "Good",
      queues: "Good",
      weather: "10C • Partly cloudy",
      overall: "Good",
    },
    preview: {
      arrivals: "111",
      departures: "93",
      terminal: "Main Terminal • Good",
      weather: "10C • Partly cloudy",
      flights: [
        "FR669 Dublin (DUB) • On time",
        "LS155 Palma (PMI) • On time",
        "FR247 Malaga (AGP) • Delayed 15m",
      ],
      queues: [
        "Security • 0-5 min",
        "Fast Track • 0-5 min",
        "Border Control • 5-10 min",
      ],
      road: [
        "M1 (J23-J24) • Good",
        "A46 (North) • Good",
        "A453 • Good",
      ],
      rail: [
        "EMR Services • On time",
        "CrossCountry • On time",
        "East Midlands Trains • On time",
      ],
    },
  },
  gatwick: {
    key: "gatwick", name: "Gatwick", code: "LGW", path: "/train-vs-taxi-gatwick", accent: "#2563eb",
    summary: "Live Gatwick rail, M25/M23 road, weather, terminal and flight intelligence.",
    status: { road: "Checking", rail: "Checking", flights: "Checking", terminals: "Checking", queues: "Checking", weather: "Checking", overall: "Checking" },
    preview: { arrivals: "—", departures: "—", terminal: "North & South terminals", weather: "Checking live weather", flights: [], queues: [], road: ["M25 to M23 • Checking", "M23 to Gatwick • Checking"], rail: ["Gatwick Airport (GTW) • Checking", "London Victoria (VIC) • Checking"] },
  },
  luton: {
    key: "luton", name: "Luton", code: "LTN", path: "/train-vs-taxi-luton-airport", accent: "#2563eb",
    summary: "Live Luton rail, M1 airport approach, weather, terminal and flight intelligence.",
    status: { road: "Checking", rail: "Checking", flights: "Checking", terminals: "Checking", queues: "Checking", weather: "Checking", overall: "Checking" },
    preview: { arrivals: "—", departures: "—", terminal: "Main terminal", weather: "Checking live weather", flights: [], queues: [], road: ["Brent Cross to M1 • Checking", "M1 to Luton Airport • Checking"], rail: ["Luton Airport Parkway (LTN) • Checking", "St Pancras (STP) • Checking"] },
  },
  stansted: {
    key: "stansted", name: "Stansted", code: "STN", path: "/train-vs-taxi-stansted", accent: "#2563eb",
    summary: "Live Stansted rail, M25/M11 road, weather, terminal and flight intelligence.",
    status: { road: "Checking", rail: "Checking", flights: "Checking", terminals: "Checking", queues: "Checking", weather: "Checking", overall: "Checking" },
    preview: { arrivals: "—", departures: "—", terminal: "Main terminal", weather: "Checking live weather", flights: [], queues: [], road: ["M25 J15 to J27 • Checking", "M11 to Stansted • Checking"], rail: ["Stansted Airport (SSD) • Checking", "Liverpool Street (LST) • Checking"] },
  },
  manchester: {
    key: "manchester", name: "Manchester", code: "MAN", path: "/train-vs-taxi-manchester-airport", accent: "#2563eb",
    summary: "Live Manchester Airport road, rail, weather, terminal and flight intelligence.",
    status: { road: "Checking", rail: "Checking", flights: "Checking", terminals: "Checking", queues: "Checking", weather: "Checking", overall: "Checking" },
    preview: { arrivals: "—", departures: "—", terminal: "Terminals 1, 2 & 3", weather: "Checking live weather", flights: [], queues: [], road: ["Airport road corridors • Checking"], rail: ["Manchester Airport station • Checking"] },
  },
};

const airportKeyByCode = {
  LHR: "heathrow",
  LGW: "gatwick",
  LTN: "luton",
  STN: "stansted",
  MAN: "manchester",
  EMA: "ema",
};

const airportPlannerOptions = [
  { key: "heathrow", terminal: "", label: "Heathrow Airport (LHR)" },
  { key: "heathrow", terminal: "2", label: "Heathrow Terminal 2 (LHR)" },
  { key: "heathrow", terminal: "3", label: "Heathrow Terminal 3 (LHR)" },
  { key: "heathrow", terminal: "4", label: "Heathrow Terminal 4 (LHR)" },
  { key: "heathrow", terminal: "5", label: "Heathrow Terminal 5 (LHR)" },
  { key: "gatwick", terminal: "", label: "Gatwick Airport (LGW)" },
  { key: "gatwick", terminal: "North", label: "Gatwick North Terminal (LGW)" },
  { key: "gatwick", terminal: "South", label: "Gatwick South Terminal (LGW)" },
  { key: "luton", terminal: "", label: "Luton Airport (LTN)" },
  { key: "stansted", terminal: "", label: "Stansted Airport (STN)" },
  { key: "manchester", terminal: "", label: "Manchester Airport (MAN)" },
  { key: "manchester", terminal: "1", label: "Manchester Terminal 1 (MAN)" },
  { key: "manchester", terminal: "2", label: "Manchester Terminal 2 (MAN)" },
  { key: "manchester", terminal: "3", label: "Manchester Terminal 3 (MAN)" },
  { key: "ema", terminal: "", label: "East Midlands Airport (EMA)" },
];

function statusRank(value) {
  const key = String(value || "").toLowerCase();
  if (key.includes("severe") || key.includes("red") || key.includes("poor")) return 5;
  if (key.includes("busy") || key.includes("moderate") || key.includes("amber")) return 4;
  if (key.includes("watch") || key.includes("light")) return 3;
  if (key.includes("good") || key.includes("green") || key.includes("on time")) return 2;
  return 1;
}

const deriveTerminalStatus = (baseAirport, preview = {}) => {
  const structured = Array.isArray(preview.terminal_pressure) ? preview.terminal_pressure : [];
  const structuredLevel = structured.length
    ? structured
        .slice()
        .sort((a, b) => (statusRank(b?.label || b?.level) || 0) - (statusRank(a?.label || a?.level) || 0))[0]
    : null;
  if (structuredLevel) return structuredLevel.label || structuredLevel.level || "Checking";
  const terminalText = String(preview.terminal || baseAirport?.preview?.terminal || "");
  if (/severe|red|poor/i.test(terminalText)) return "Severe";
  if (/busy|moderate|amber|pressure/i.test(terminalText)) return "Busy";
  if (/checking|updating/i.test(terminalText)) return "Checking";
  return "Good";
};

const deriveQueueStatus = (baseAirport, preview = {}) => {
  const queues = Array.isArray(preview.terminal_queues) ? preview.terminal_queues : [];
  const levels = queues.map((row) => String(row?.level || row?.label || "").toLowerCase());
  if (levels.some((level) => level.includes("red") || level.includes("severe"))) return "Busy";
  if (levels.some((level) => level.includes("amber") || level.includes("moderate") || level.includes("busy"))) return "Busy";
  if (levels.some((level) => level.includes("unknown") || level.includes("checking"))) return "Checking";
  return "Good";
};

const normalizeLiveAirport = (baseAirport, liveAirport) => {
  if (!liveAirport || !baseAirport) return baseAirport;
  const hero = liveAirport.hero_status || {};
  const preview = liveAirport.preview || {};
  const normalizedPreview = {
    arrivals: preview.arrivals || baseAirport.preview.arrivals,
    departures: preview.departures || baseAirport.preview.departures,
    terminal: preview.terminal || baseAirport.preview.terminal,
    weather: preview.weather || baseAirport.preview.weather,
    flights: Array.isArray(preview.flights) && preview.flights.length ? preview.flights : baseAirport.preview.flights,
    queues: Array.isArray(preview.queues) && preview.queues.length ? preview.queues : baseAirport.preview.queues,
    terminal_queues: Array.isArray(preview.terminal_queues) ? preview.terminal_queues : baseAirport.preview.terminal_queues || [],
    terminal_pressure: Array.isArray(preview.terminal_pressure) ? preview.terminal_pressure : baseAirport.preview.terminal_pressure || [],
    road: Array.isArray(preview.road) && preview.road.length ? preview.road : baseAirport.preview.road,
    rail: Array.isArray(preview.rail) && preview.rail.length ? preview.rail : baseAirport.preview.rail,
  };
  return {
    ...baseAirport,
    miniStatus: Array.isArray(liveAirport.mini_status) ? liveAirport.mini_status : baseAirport.miniStatus || [],
    detailedStatus: liveAirport.detailed_status || baseAirport.detailedStatus || {},
    liveReason: liveAirport.overall?.reason || baseAirport.liveReason,
    liveSummary: liveAirport.summary?.headline || baseAirport.liveSummary,
    nextStep: liveAirport.summary?.next_step || baseAirport.nextStep,
    nextStepDetail: liveAirport.summary?.detail || baseAirport.nextStepDetail,
    status: {
      road: hero.road?.label || baseAirport.status.road,
      rail: hero.rail?.label || baseAirport.status.rail,
      flights: hero.flights?.label || baseAirport.status.flights,
      terminals: hero.terminals?.label || hero.terminal?.label || deriveTerminalStatus(baseAirport, normalizedPreview),
      queues: hero.queues?.label || deriveQueueStatus(baseAirport, normalizedPreview),
      weather: hero.weather?.label || baseAirport.status.weather,
      overall: liveAirport.overall?.label || baseAirport.status.overall,
    },
    statusReasons: {
      road: hero.road?.reason || hero.road?.headline || "",
      rail: hero.rail?.reason || hero.rail?.headline || "",
      flights: hero.flights?.reason || hero.flights?.headline || "",
      terminals: hero.terminals?.reason || hero.terminal?.reason || normalizedPreview.terminal || "",
      queues: hero.queues?.reason || normalizedPreview.queues?.[0] || "",
      weather: hero.weather?.reason || hero.weather?.headline || "",
      overall: liveAirport.overall?.reason || "",
    },
    statusHeadlines: {
      road: shortStatusHeadline("road", hero.road?.headline || hero.road?.reason || ""),
      rail: shortStatusHeadline("rail", hero.rail?.headline || hero.rail?.reason || ""),
      flights: shortStatusHeadline("flights", hero.flights?.headline || hero.flights?.reason || ""),
      terminals: shortStatusHeadline("terminals", hero.terminals?.headline || hero.terminal?.headline || normalizedPreview.terminal || ""),
      queues: shortStatusHeadline("queues", hero.queues?.headline || normalizedPreview.queues?.[0] || ""),
      weather: shortStatusHeadline("weather", hero.weather?.headline || hero.weather?.reason || ""),
    },
    preview: normalizedPreview,
  };
};

const shortStatusHeadline = (key, value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  if (key === "road") {
    const segmentMatch = text.match(/most affected segment\s+([^.;]+)/i);
    if (segmentMatch?.[1]) return `${segmentMatch[1].trim()} delays`;
    const deltaMatch = text.match(/:\s*([^.;]+)/);
    if (deltaMatch?.[1]) return deltaMatch[1].trim();
  }
  if (key === "rail") {
    return text.replace(/^([^:]+):\s*/, "").replace(/\s*\|\s*/g, " / ");
  }
  if (key === "flights") {
    if (text.includes("•")) return text;
    const delayedMatch = text.match(/(\d+\s+delayed,\s+\d+\s+cancelled)/i);
    if (delayedMatch?.[1]) return delayedMatch[1];
    return text.replace(/\s+in\s+the\s+.+$/i, "");
  }
  if (key === "weather") {
    return text.split(";")[0].trim();
  }
  if (key === "terminals" || key === "queues") {
    return text.length > 42 ? `${text.slice(0, 39).trim()}...` : text;
  }
  return text.length > 42 ? `${text.slice(0, 39).trim()}...` : text;
};

const statusLevelWord = (value) => String(value || "checking").toLowerCase();

const airportPhrase = (airport) => (airport?.code === "LHR" ? "Heathrow" : airport?.code === "EMA" ? "East Midlands Airport" : "the airport");

const readableRoadText = (airport, detail = "") => {
  const text = String(detail || "").trim();
  const airportName = airportPhrase(airport);
  const count = text.match(/(\d+)\s+monitored\s+road\s+corridors/i)?.[1];
  const worst = text.match(/Worst corridor\s+([^.;]+)/i)?.[1];
  const delay = text.match(/is\s+([+-]?\d+\s*m)\s+vs\s+usual/i)?.[1];
  const level = statusLevelWord(airport?.status?.road);

  if (level === "good") {
    if (worst && delay && !delay.startsWith("-") && !delay.startsWith("+0")) {
      return `Roads and motorways to ${airportName} look clear overall. Slowest route: ${worst.trim()} (${delay.replace("+", "")} slower than usual).`;
    }
    return `Roads and motorways to ${airportName} look clear across ${count || "the"} monitored routes.`;
  }
  if (level === "moderate") {
    return worst
      ? `Some delays on roads and motorways to ${airportName}. Slowest route: ${worst.trim()}${delay ? ` (${delay.replace("+", "")} slower than usual)` : ""}.`
      : `Some delays on roads and motorways to ${airportName}. Allow extra time.`;
  }
  if (level === "poor" || level === "severe") {
    return worst
      ? `Heavy delays on roads and motorways to ${airportName}. Main issue: ${worst.trim()}.`
      : `Heavy delays on roads and motorways to ${airportName}. Check before you leave.`;
  }
  return `Road and motorway checks for ${airportName} are updating.`;
};

const readableRailText = (airport, detail = "") => {
  const text = String(detail || "").trim();
  const airportName = airportPhrase(airport);
  const count = text.match(/(\d+)\s+rail\s+board\/service\s+checks/i)?.[1];
  const level = statusLevelWord(airport?.status?.rail);

  if (level === "good") return `Train services we track for ${airportName} look normal${count ? ` across ${count} checks` : ""}.`;
  if (level === "moderate") return `Some train services for ${airportName} may be delayed or disrupted. Check your route before travelling.`;
  if (level === "poor" || level === "severe") return `Major train disruption may affect journeys to ${airportName}. Check alternatives before travelling.`;
  return `Train service checks for ${airportName} are updating.`;
};

const readableFlightText = (airport, detail = "") => {
  const text = String(detail || "").trim();
  const airportName = airportPhrase(airport);
  const cancelled = text.match(/(\d+)\s+cancelled/i)?.[1];
  const delayed30 = text.match(/(\d+)\s+delayed\s+30m\+/i)?.[1];
  const delayed60 = text.match(/(\d+)\s+delayed\s+60m\+/i)?.[1];
  const parts = [];
  if (cancelled) parts.push(`${cancelled} cancelled`);
  if (delayed30) parts.push(`${delayed30} delayed 30+ min`);
  if (delayed60) parts.push(`${delayed60} delayed 60+ min`);
  if (parts.length) return `Flight disruption at ${airportName}: ${parts.join(", ")}.`;
  if (text) return text.replace(/\s+in\s+the\s+.+$/i, "");
  return `Flight checks for ${airportName} are updating.`;
};

const readableWeatherText = (airport, detail = "") => {
  const text = String(detail || "").trim();
  if (text) return `Weather near ${airportPhrase(airport)}: ${text.split(";")[0].trim()}.`;
  return `Weather checks for ${airportPhrase(airport)} are updating.`;
};

const readableTerminalText = (airport, detail = "") => {
  const text = String(detail || "").trim();
  if (text && !/checking|updating/i.test(text)) return text;
  return airport?.code === "LHR" ? "Terminal pressure monitored across T2, T3, T4 and T5." : "Terminal operations look normal.";
};

const readableQueueText = (airport, detail = "") => {
  const text = String(detail || "").trim();
  if (text && !/checking|updating/i.test(text)) return text;
  return airport?.code === "LHR" ? "Security and border queues monitored by terminal." : "Terminal queues look quiet.";
};

const readableOverallText = (airport, detail = "") => {
  const text = String(detail || "").trim();
  const level = statusLevelWord(airport?.status?.overall);
  const airportName = airportPhrase(airport);
  if (/flights currently set/i.test(text)) return `Flight delays are the main thing affecting ${airportName} right now.`;
  if (/road/i.test(text)) return `Road and motorway conditions are the main thing affecting ${airportName} right now.`;
  if (/rail/i.test(text)) return `Train disruption is the main thing affecting ${airportName} right now.`;
  if (/weather/i.test(text)) return `Weather is the main thing affecting ${airportName} right now.`;
  if (level === "good") return `${airportName} looks good overall.`;
  if (level === "moderate") return `${airportName} has some disruption. Check the details before travelling.`;
  if (level === "poor" || level === "severe") return `${airportName} has significant disruption. Check your journey before travelling.`;
  return `Live status for ${airportName} is updating.`;
};

const readableStatusText = (airport, key, detail = "") => {
  if (key === "road") return readableRoadText(airport, detail);
  if (key === "rail") return readableRailText(airport, detail);
  if (key === "flights") return readableFlightText(airport, detail);
  if (key === "terminals") return readableTerminalText(airport, detail);
  if (key === "queues") return readableQueueText(airport, detail);
  if (key === "weather") return readableWeatherText(airport, detail);
  if (key === "overall") return readableOverallText(airport, detail);
  return String(detail || "").trim();
};

const readableStatusHeadline = (airport, key) => {
  const detail = airport?.statusReasons?.[key] || airport?.statusHeadlines?.[key] || "";
  return readableStatusText(airport, key, detail);
};

const checkCards = [
  {
    icon: "✈️",
    title: "Flights & terminals",
    body: "Live arrivals and departures, terminal changes, gate updates and delay context.",
  },
  {
    icon: "🚆",
    title: "Rail & station boards",
    body: "Real-time train times, platforms, service disruption and transfer connections.",
  },
  {
    icon: "🚗",
    title: "Motorways & road corridors",
    body: "Live traffic, incidents, corridor summaries and timing pressure on key approaches.",
  },
  {
    icon: "🅿️",
    title: "Fares & parking",
    body: "Parking references, transfer assumptions, drop-off context and mode comparison inputs.",
  },
  {
    icon: "🌤️",
    title: "Weather & disruption signals",
    body: "Airport and home weather, visibility, wind and travel-impact conditions.",
  },
  {
    icon: "👥",
    title: "Queues & terminal flow",
    body: "Security, border control and terminal pressure summaries where available.",
  },
];

const helpSteps = [
  {
    title: "Enter journey details",
    body: "Choose your airport, add your postcode and optionally include a flight number.",
  },
  {
    title: "We check live conditions",
    body: "Road, rail, weather and flight sources are combined against the airport journey model.",
  },
  {
    title: "Open the planner",
    body: "You move into the airport-specific planner with the right flow, checks and live logic.",
  },
];

const coverageLive = ["Heathrow (LHR)", "Gatwick (LGW)", "Luton (LTN)", "Stansted (STN)", "Manchester (MAN)", "East Midlands (EMA)"];
const coverageSoon = ["London City (LCY)", "Birmingham (BHX)"];
const heroChips = ["Live journey conditions", "Trusted travel sources", "Alerts when plans change"];
const heroMetrics = [
  { value: "6", label: "Airports covered", icon: "⌁" },
  { value: "6", label: "Live signal types", icon: "◎" },
  { value: "1–2 min", label: "Update cycle", icon: "◔" },
  { value: "3", label: "Travel modes", icon: "◈" },
  { value: "1", label: "Combined journey plan", icon: "◉" },
];
const sourceList = [
  { name: "National Rail", mark: "✺" },
  { name: "National Highways", mark: "▲" },
  { name: "Met Office", mark: "≈" },
  { name: "Heathrow", mark: "◌" },
  { name: "Airport Systems", mark: "□" },
  { name: "& more", mark: "+" },
];
const sourceIcons = {
  road: "🚘",
  rail: "🚆",
  flights: "✈️",
  terminals: "🏢",
  queues: "👥",
  weather: "☁️",
  overall: "⏱️",
};

const statusTone = (value) => {
  const text = String(value || "").toLowerCase();
  if (text.includes("good") || text.includes("on time") || text.includes("clear") || text.includes("schedule matched") || text.includes("timetable found") || text === "normal" || text === "green") return { bg: "#dcfce7", fg: "#166534" };
  if (text.includes("watch") || text.includes("manually") || text === "light_green") return { bg: "#e0f2fe", fg: "#075985" };
  if (text.includes("moderate") || text.includes("busy") || text.includes("traffic +") || text === "amber") return { bg: "#fef3c7", fg: "#92400e" };
  if (text.includes("severe") || text === "red" || text === "critical_red") return { bg: "#fee2e2", fg: "#991b1b" };
  return { bg: "#e5e7eb", fg: "#374151" };
};

// Source adapters retain their native vocabulary; the homepage presents one
// predictable traveller-facing taxonomy.
const statusLabel = (value) => {
  const text = String(value || "").toLowerCase();
  if (!text || text.includes("unknown") || text.includes("checking") || text.includes("updating")) return "Checking…";
  if (text.includes("severe") || text.includes("cancel") || text.includes("red") || text.includes("critical") || text.includes("poor")) return "Disrupted";
  if (text.includes("moderate") || text.includes("busy") || text.includes("amber") || text.includes("watch") || text.includes("delay") || text.includes("traffic +")) return "Watch";
  if (text.includes("good") || text.includes("green") || text.includes("on time") || text.includes("clear") || text.includes("normal") || text.includes("matched") || text.includes("found")) return "Good";
  return "Checking…";
};

const statusSurface = (value) => {
  const text = String(value || "").toLowerCase();
  if (text.includes("severe") || text === "red" || text === "critical_red") {
    return { bg: "linear-gradient(180deg, #fff8f8 0%, #fff1f2 100%)", border: "#fecaca" };
  }
  if (text.includes("moderate") || text.includes("busy") || text === "amber") {
    return { bg: "linear-gradient(180deg, #fffdf7 0%, #fff7ed 100%)", border: "#fed7aa" };
  }
  if (text.includes("watch") || text === "light_green") {
    return { bg: "linear-gradient(180deg, #f8fcff 0%, #f0f9ff 100%)", border: "#bae6fd" };
  }
  if (text.includes("good") || text.includes("on time") || text === "green") {
    return { bg: "linear-gradient(180deg, #fbfffd 0%, #f0fdf4 100%)", border: "#bbf7d0" };
  }
  return { bg: "#ffffff", border: "#dbe7ff" };
};

const previewCardShell = (isCompact) => ({
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  background: "rgba(255,255,255,0.92)",
  border: "1px solid rgba(190, 208, 236, 0.95)",
  borderRadius: isCompact ? 16 : 18,
  padding: isCompact ? 12 : 16,
  color: "#08204a",
  boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
});

const previewSectionShell = (status, isCompact) => {
  const surface = statusSurface(status);
  return {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    overflow: "hidden",
    border: `${isCompact ? 1.5 : 1}px solid ${surface.border}`,
    borderRadius: isCompact ? 14 : 16,
    padding: isCompact ? 13 : 13,
    background: surface.bg,
    alignSelf: "start",
    boxShadow: isCompact ? "0 7px 18px rgba(15, 35, 70, 0.07)" : "0 8px 18px rgba(15, 23, 42, 0.04)",
  };
};

const previewSectionHeader = (status, isCompact) => {
  const text = String(status || "").toLowerCase();
  let background = "#eef3f8";
  let border = "#d4deea";
  if (text.includes("severe") || text === "red" || text === "critical_red") {
    background = "#ffe8e8";
    border = "#f2c2c2";
  } else if (text.includes("moderate") || text.includes("busy") || text === "amber") {
    background = "#ffefcc";
    border = "#e4cd99";
  } else if (text.includes("watch") || text === "light_green") {
    background = "#e5f2f9";
    border = "#bfd6e5";
  } else if (text.includes("good") || text.includes("on time") || text === "green") {
    background = "#e5f7eb";
    border = "#bcdcc6";
  }
  return {
    margin: isCompact ? "-13px -13px 10px" : "-13px -13px 12px",
    padding: isCompact ? "11px 13px" : "12px 13px",
    background,
    borderBottom: `1px solid ${border}`,
  };
};

function StatusPill({ status, compact = false }) {
  const tone = statusTone(status);
  return (
    <span
      style={{
        flex: "0 0 auto",
        minWidth: compact ? 70 : 82,
        textAlign: "center",
        padding: compact ? "3px 7px" : "4px 8px",
        borderRadius: 999,
        background: tone.bg,
        color: tone.fg,
        fontSize: compact ? 9 : 10,
        fontWeight: 900,
        textTransform: "uppercase",
        boxSizing: "border-box",
      }}
    >
      {statusLabel(status)}
    </span>
  );
}

const useCompactMobile = (maxWidth = 760) => {
  const getMatch = () => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia(`(max-width: ${maxWidth}px)`).matches;
  };
  const [isCompact, setIsCompact] = useState(getMatch);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const onChange = (e) => setIsCompact(!!e.matches);
    setIsCompact(mq.matches);
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, [maxWidth]);

  return isCompact;
};

const londonLocalToUtcIso = (value) => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/);
  if (!match) return "";
  const desired = match.slice(1).map(Number);
  let guess = Date.UTC(desired[0], desired[1] - 1, desired[2], desired[3], desired[4]);
  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  });
  for (let pass = 0; pass < 2; pass += 1) {
    const parts = Object.fromEntries(formatter.formatToParts(new Date(guess)).map((part) => [part.type, part.value]));
    const represented = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour), Number(parts.minute));
    const wanted = Date.UTC(desired[0], desired[1] - 1, desired[2], desired[3], desired[4]);
    guess += wanted - represented;
  }
  return new Date(guess).toISOString();
};

const londonNowLocalInput = () => {
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(new Date()).map((part) => [part.type, part.value]));
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};

function HeroPlannerCard({ airportKey, airportList, form, onAirportChange, onChange, onSubmit, loading = false, error = "", lockedAirport = false, lockedDirection = "", addressPlaceholder = "", addressLabel = "" }) {
  const isCompact = useCompactMobile();
  // Some iPhone/Safari configurations expose a wider CSS viewport even though
  // the page is still in the mobile layout. Keep the compact form tied to the
  // same mobile breakpoint so its controls do not fall back to tablet sizing.
  const isNarrow = isCompact;
  const isReturn = form.direction === "return";
  const googleAddressAutocompleteEnabled = process.env.REACT_APP_USE_AUTOCOMPLETE === "true" && Boolean(process.env.REACT_APP_GOOGLE_MAPS_API_KEY);
  const selectedAirport = airportList.find((airport) => airport.key === airportKey) || airportList[0];
  const supportsScheduledFlights = ["heathrow", "ema", "manchester", "stansted", "gatwick", "luton"].includes(airportKey);
  const [postcodeSuggestions, setPostcodeSuggestions] = useState([]);
  const [flightSuggestions, setFlightSuggestions] = useState([]);
  const [flightSchedules, setFlightSchedules] = useState([]);
  const [selectedFlightNumber, setSelectedFlightNumber] = useState("");
  const [postcodeFocused, setPostcodeFocused] = useState(false);
  const [flightFocused, setFlightFocused] = useState(false);
  const [airportFocused, setAirportFocused] = useState(false);
  const [airportQuery, setAirportQuery] = useState("");
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [manualFlightTime, setManualFlightTime] = useState(false);
  const [manualTimeError, setManualTimeError] = useState("");
  const [addressResolutionNote, setAddressResolutionNote] = useState("");
  const addressAutocompleteRef = useRef(null);
  const normalizedTypedFlight = String(form.flight || "").replace(/\s+/g, "").toUpperCase();
  const typedFlightShapeIsValid = /^(?=.{3,8}$)[A-Z0-9]{2,3}\d{1,4}[A-Z]?$/.test(normalizedTypedFlight);
  const typedFlightIsConfirmed = normalizedTypedFlight.length >= 5 || flightSuggestions.includes(normalizedTypedFlight);
  const exactTypedFlight = typedFlightShapeIsValid && typedFlightIsConfirmed ? normalizedTypedFlight : "";
  const scheduleLookupFlight = String(selectedFlightNumber || exactTypedFlight).replace(/\s+/g, "").toUpperCase();

  useEffect(() => { setManualFlightTime(false); setManualTimeError(""); }, [airportKey, isReturn]);

  useEffect(() => {
    const query = String(form.pickup_address || form.postcode || "").trim();
    if (googleAddressAutocompleteEnabled || !postcodeFocused || query.length < 2) {
      setPostcodeSuggestions([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const response = await api.get("/api/postcodes/suggest", { params: { q: query, limit: 8 } });
        if (cancelled) return;
        const unique = new Map();
        (response?.data?.suggestions || []).forEach((item) => {
          const normalized = String(item || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
          if (!normalized || unique.has(normalized)) return;
          unique.set(normalized, normalized.length > 3 ? `${normalized.slice(0, -3)} ${normalized.slice(-3)}` : normalized);
        });
        setPostcodeSuggestions([...unique.values()].slice(0, 8));
      } catch (_error) {
        if (!cancelled) setPostcodeSuggestions([]);
      }
    }, 180);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [form.pickup_address, form.postcode, postcodeFocused, googleAddressAutocompleteEnabled]);

  const extractUkPostcode = (value = "") => String(value || "").toUpperCase().match(/\b(?:GIR\s?0AA|(?:[A-Z]{1,2}\d[A-Z\d]?|[A-Z]{1,2}\d{1,2})\s?\d[A-Z]{2})\b/)?.[0] || "";
  const postcodeFromComponents = (components = []) => extractUkPostcode(String((components || []).find((component) => component?.types?.includes("postal_code"))?.long_name || ""));
  const reverseResolvePostcode = (place) => new Promise((resolve) => {
    const googleMaps = typeof window !== "undefined" ? window.google?.maps : null;
    const location = place?.geometry?.location;
    if (!googleMaps?.Geocoder || !location) return resolve("");
    const geocoder = new googleMaps.Geocoder();
    geocoder.geocode({ location }, (results, status) => {
      if (status !== "OK" || !Array.isArray(results)) return resolve("");
      const postcode = results.map((result) => postcodeFromComponents(result?.address_components) || extractUkPostcode(result?.formatted_address)).find(Boolean) || "";
      resolve(postcode);
    });
  });
  const handleAddressSelected = async (selectedPlace = null) => {
    const place = selectedPlace || addressAutocompleteRef.current?.getPlace?.();
    const formattedAddress = String(place?.formatted_address || place?.name || "").trim();
    let postcode = postcodeFromComponents(place?.address_components) || extractUkPostcode(formattedAddress);
    if (formattedAddress) onChange("pickup_address", formattedAddress);
    onChange("place_id", place?.place_id || "");
    onChange("latitude", Number(place?.geometry?.location?.lat?.()) || "");
    onChange("longitude", Number(place?.geometry?.location?.lng?.()) || "");
    if (!postcode) postcode = await reverseResolvePostcode(place);
    onChange("postcode", postcode);
    const selectedTypes = Array.isArray(place?.types) ? place.types : [];
    const isBroadPlace = selectedTypes.some((type) => ["locality", "postal_town", "administrative_area_level_1", "administrative_area_level_2"].includes(type));
    setAddressResolutionNote(postcode ? (isBroadPlace ? `City-centre estimate (${postcode}) — choose a full address for accurate prices` : "") : "Please choose a full address or postcode");
    setPostcodeSuggestions([]);
    setPostcodeFocused(false);
    trackFunnelEvent("ajp_address_selected", {
      brand: "planner", tracking_source: "ajp_home", airport: selectedAirport?.code,
      address_source: "google", postcode: postcode || undefined,
      has_place_id: Boolean(place?.place_id), direction: isReturn ? "from_airport" : "to_airport",
    });
  };

  useEffect(() => {
    const query = String(form.flight || "").replace(/\s+/g, "").toUpperCase();
    if (!supportsScheduledFlights || !flightFocused || query.length < 2) {
      setFlightSuggestions([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const response = await api.get("/api/ajp/v2/flights/suggest", { params: { q: query, limit: 8, direction: isReturn ? "arrival" : "departure", airport_code: selectedAirport?.code } });
        if (!cancelled) {
          const suggestions = (response?.data?.suggestions || []).map((item) => String(item || "").toUpperCase()).filter(Boolean);
          setFlightSuggestions(suggestions);
          trackFunnelEvent("ajp_flight_searched", { brand: "planner", tracking_source: "ajp_home", airport: selectedAirport?.code, direction: isReturn ? "from_airport" : "to_airport", search_length: query.length, result_count: suggestions.length });
        }
      } catch (searchError) {
        if (!cancelled) {
          setFlightSuggestions([]);
          trackFunnelEvent("ajp_api_error", { brand: "planner", tracking_source: "ajp_home", airport: selectedAirport?.code, error_stage: "flight_suggest", error_code: searchError?.code || "flight_suggest_failed", http_status: searchError?.response?.status });
        }
      }
    }, 180);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [airportKey, form.flight, flightFocused, supportsScheduledFlights, isReturn, selectedAirport?.code]);

  useEffect(() => {
    const flightNumber = scheduleLookupFlight;
    if (!supportsScheduledFlights || flightNumber.length < 3) {
      setFlightSchedules([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setScheduleLoading(true);
      try {
        const response = await api.get("/api/ajp/v2/flights/schedules", { params: { flight_number: flightNumber, direction: isReturn ? "arrival" : "departure", airport_code: selectedAirport?.code } });
        if (cancelled) return;
        const rows = (response?.data?.schedules || []).filter((row) => {
          const anchor = row?.anchor_time_utc || row?.estimated_time_utc || row?.scheduled_anchor_utc || row?.scheduled_time_utc;
          return anchor && new Date(anchor).getTime() > Date.now() + 60000;
        });
        setFlightSchedules(rows);
        trackFunnelEvent(rows.length ? "ajp_flight_found" : "ajp_flight_not_found", { brand: "planner", tracking_source: "ajp_home", airport: selectedAirport?.code, flight_number: flightNumber, direction: isReturn ? "from_airport" : "to_airport", result_count: rows.length });
      } catch (scheduleError) {
        if (!cancelled) {
          setFlightSchedules([]);
          trackFunnelEvent("ajp_api_error", { brand: "planner", tracking_source: "ajp_home", airport: selectedAirport?.code, flight_number: flightNumber, error_stage: "flight_schedule", error_code: scheduleError?.code || "flight_schedule_failed", http_status: scheduleError?.response?.status });
        }
      } finally {
        if (!cancelled) setScheduleLoading(false);
      }
    }, 700);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [airportKey, scheduleLookupFlight, supportsScheduledFlights, isReturn, selectedAirport?.code]);

  const scheduleAnchor = (row) => row?.anchor_time_utc || row?.estimated_time_utc || row?.scheduled_anchor_utc || row?.scheduled_time_utc || "";
  const scheduleTerminal = (row) => String(row?.terminal || row?.arrival_terminal || row?.departure_terminal || "").replace(/^terminal\s*/i, "").replace(/^t(?=\d)/i, "").trim();
  const scheduleDate = (row) => {
    const date = new Date(scheduleAnchor(row));
    if (Number.isNaN(date.getTime())) return "";
    const parts = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(date);
    return `${parts.find((part) => part.type === "year")?.value}-${parts.find((part) => part.type === "month")?.value}-${parts.find((part) => part.type === "day")?.value}`;
  };
  const scheduleLabel = (row) => new Date(scheduleAnchor(row)).toLocaleString("en-GB", { timeZone: "Europe/London", weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false });
  const useManualFlightTime = manualFlightTime || !String(form.flight || "").trim();
  const minimumManualLocal = londonNowLocalInput();
  const compactInputStyle = {
    width: "100%",
    minHeight: isNarrow ? 42 : isCompact ? 54 : 60,
    boxSizing: "border-box",
    borderRadius: 10,
    border: "1px solid #d7e1ef",
    background: "#ffffff",
    color: "#071948",
    padding: isNarrow ? "14px 9px 3px" : isCompact ? "16px 13px 8px" : "18px 16px 10px",
    fontSize: isNarrow ? 11 : isCompact ? 14 : 15,
    fontWeight: 750,
    outline: "none",
  };
  const labelStyle = {
    position: "absolute",
    top: isNarrow ? 3 : isCompact ? 6 : 8,
    left: isNarrow ? 8 : isCompact ? 13 : 16,
    zIndex: 2,
    padding: isNarrow ? "0 3px" : 0,
    borderRadius: 4,
    background: isNarrow ? "#ffffff" : "transparent",
    color: isNarrow ? "#405779" : "#526783",
    fontSize: isNarrow ? 9 : isCompact ? 10 : 11,
    lineHeight: 1.1,
    fontWeight: 800,
    pointerEvents: "none",
  };
  const addressInput = (
    <input
      value={form.pickup_address || form.postcode}
      onChange={(event) => { const value = event.target.value; onChange("pickup_address", value); onChange("postcode", extractUkPostcode(value)); onChange("place_id", ""); onChange("latitude", ""); onChange("longitude", ""); setAddressResolutionNote(""); }}
      onFocus={() => setPostcodeFocused(true)}
      onBlur={() => setTimeout(() => setPostcodeFocused(false), 120)}
      autoComplete="off"
      aria-label={isReturn ? "Destination address or postcode" : "Pickup address or postcode"}
      placeholder={addressPlaceholder || (isReturn ? "Start typing your home address" : "Start typing address or postcode")}
      className="ajp-mobile-field"
      style={compactInputStyle}
    />
  );
  const availableAirportOptions = lockedAirport
    ? airportPlannerOptions.filter((option) => option.key === airportKey)
    : airportPlannerOptions;
  const selectedAirportOption = availableAirportOptions.find((option) => option.key === airportKey && String(option.terminal || "") === String(form.terminal || "")) || availableAirportOptions.find((option) => option.key === airportKey && !option.terminal);
  const filteredAirportOptions = availableAirportOptions.filter((option) => option.label.toLowerCase().includes(airportQuery.trim().toLowerCase()));
  const chooseAirportOption = (option) => {
    onAirportChange(option.key, option.terminal || "");
    trackFunnelEvent("ajp_airport_terminal_selected", { brand: "planner", tracking_source: "ajp_home", airport: airportList.find((item) => item.key === option.key)?.code, terminal: option.terminal || "unknown", direction: isReturn ? "from_airport" : "to_airport" });
    setAirportQuery("");
    setAirportFocused(false);
  };

  return (
    <section
      className="ajp-home-form"
      style={{
        marginTop: isNarrow ? 8 : isCompact ? 12 : 28,
        background:
          "radial-gradient(circle at 80% 0%, rgba(37,99,235,0.22), transparent 32%), linear-gradient(145deg, #08245f 0%, #061b46 52%, #082a63 100%)",
        border: "1px solid rgba(191, 219, 254, 0.34)",
        borderRadius: isNarrow ? 15 : 18,
        padding: isNarrow ? 10 : isCompact ? 14 : 24,
        color: "#eff6ff",
        boxShadow: "0 22px 48px rgba(29, 78, 216, 0.24)",
      }}
    >
      <div style={{ color: "#1d7cff", fontSize: isNarrow ? 11 : 13, fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.03em" }}>
        Plan your journey
      </div>
      <div role="tablist" aria-label="Journey direction" style={{ marginTop: isNarrow ? 7 : isCompact ? 9 : 12, display: "grid", gridTemplateColumns: lockedDirection ? "1fr" : "repeat(2,minmax(0,1fr))", maxWidth: isCompact ? "100%" : 430, padding: isNarrow ? 3 : 4, borderRadius: 12, background: "rgba(3,16,45,.58)", border: "1px solid rgba(147,197,253,.24)" }}>
        {[{ key: "outbound", label: "To the airport", icon: "✈" }, { key: "return", label: "From the airport", icon: "⌂" }].filter((option) => !lockedDirection || option.key === lockedDirection).map((option) => {
          const active = (form.direction || "outbound") === option.key;
          return <button className="ajp-direction-tab" key={option.key} type="button" role="tab" aria-selected={active} onClick={() => { onChange("direction", option.key); onChange("flight", ""); onChange("date", ""); onChange("flight_anchor_utc", ""); onChange("manual_local", ""); }} style={{ minWidth: 0, minHeight: isNarrow ? 36 : isCompact ? 40 : 44, padding: isNarrow ? 0 : undefined, display: "flex", alignItems: "center", justifyContent: "center", gap: isNarrow ? 3 : 4, overflow: "hidden", border: 0, borderRadius: 9, background: active ? "#fff" : "transparent", color: active ? "#08245f" : "#cfe1ff", fontSize: isNarrow ? 9 : isCompact ? 13 : 14, letterSpacing: isNarrow ? "-.01em" : 0, lineHeight: 1.1, whiteSpace: "nowrap", fontWeight: 900, cursor: "pointer", boxShadow: active ? "0 8px 18px rgba(0,0,0,.16)" : "none" }}><span aria-hidden="true">{option.icon}</span><span>{option.label}</span></button>;
        })}
      </div>
      <h2 style={{ margin: isNarrow ? "7px 0 2px" : isCompact ? "9px 0 3px" : "10px 0 4px", fontSize: isNarrow ? 16 : isCompact ? 19 : 22, lineHeight: 1.15 }}>
        {isReturn ? `How will you travel home from ${selectedAirport?.name}?` : "Where are you travelling from?"}
      </h2>
      <p style={{ margin: 0, color: "#bdd2f5", fontSize: isNarrow ? 9 : isCompact ? 11 : 13, lineHeight: 1.35 }}>
        {isReturn ? "Add your destination and, if you know it, your arriving flight. You can enter the arrival time manually instead." : "Add your postcode and, if you know it, your departing flight. Or enter the flight departure time manually and we’ll work out when to leave."}
      </p>

      <div
        style={{
          marginTop: isNarrow ? 8 : isCompact ? 12 : 16,
          display: "grid",
          gridTemplateColumns: isCompact ? "1fr" : "minmax(210px, 1.15fr) minmax(190px, 1fr) minmax(150px, .78fr) minmax(160px, .82fr) minmax(170px, .8fr)",
          gap: isNarrow ? 5 : isCompact ? 8 : 14,
          alignItems: "start",
        }}
      >
        <label style={{ position: "relative", display: "block", order: isReturn ? 2 : 1 }}>
          <span style={labelStyle}>{addressLabel || (isReturn ? "Travelling to" : "Travelling from")}</span>
          {googleAddressAutocompleteEnabled ? (
            <Autocomplete
              onLoad={(instance) => { addressAutocompleteRef.current = instance; }}
              onPlaceChanged={() => handleAddressSelected()}
              restrictions={{ country: ["gb"] }}
              options={{ types: ["geocode", "establishment"], fields: ["formatted_address", "address_components", "geometry", "name", "place_id", "types"] }}
            >
              {addressInput}
            </Autocomplete>
          ) : addressInput}
          {postcodeFocused && postcodeSuggestions.length ? (
            <div role="listbox" style={{ position: "absolute", zIndex: 20, top: "calc(100% + 5px)", left: 0, right: 0, borderRadius: 10, border: "1px solid #c9d8ef", background: "#fff", boxShadow: "0 14px 30px rgba(15,23,42,.18)", overflow: "hidden" }}>
              {postcodeSuggestions.map((postcode) => (
                <button key={postcode} type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => { onChange("postcode", postcode); onChange("pickup_address", postcode); setPostcodeSuggestions([]); setPostcodeFocused(false); trackFunnelEvent("ajp_address_selected", { brand: "planner", tracking_source: "ajp_home", airport: selectedAirport?.code, address_source: "postcode_io", postcode, has_place_id: false, direction: isReturn ? "from_airport" : "to_airport" }); }} style={{ display: "block", width: "100%", padding: "10px 12px", border: 0, borderTop: "1px solid #eef2f7", background: "#fff", color: "#0f2f63", textAlign: "left", fontWeight: 750, cursor: "pointer" }}>{postcode}</button>
              ))}
            </div>
          ) : null}
        </label>
        <label style={{ position: "relative", display: "block", order: isReturn ? 1 : 2 }}>
          <span style={labelStyle}>{isReturn ? "Arriving at" : "Flying from"}</span>
          <input
            role="combobox"
            aria-expanded={airportFocused}
            aria-controls="ajp-airport-options"
            aria-autocomplete="list"
            value={airportFocused ? airportQuery : selectedAirportOption?.label || ""}
            onChange={(event) => { setAirportQuery(event.target.value); setAirportFocused(true); }}
            onFocus={() => { setAirportQuery(""); setAirportFocused(true); }}
            onBlur={() => setTimeout(() => setAirportFocused(false), 140)}
            onKeyDown={(event) => {
              if (event.key === "Escape") { setAirportFocused(false); setAirportQuery(""); event.currentTarget.blur(); }
              if (event.key === "Enter" && filteredAirportOptions[0]) { event.preventDefault(); chooseAirportOption(filteredAirportOptions[0]); }
            }}
            placeholder="Search airport or terminal"
            className="ajp-mobile-field"
            style={{ ...compactInputStyle, paddingRight: 38 }}
          />
          <span style={{ position: "absolute", right: 14, top: 22, color: "#526783", pointerEvents: "none" }}>⌄</span>
          {airportFocused ? (
            <div id="ajp-airport-options" role="listbox" style={{ position: "absolute", zIndex: 30, top: "calc(100% + 5px)", left: 0, right: 0, maxHeight: 310, overflowY: "auto", borderRadius: 11, border: "1px solid #c9d8ef", background: "#fff", boxShadow: "0 16px 34px rgba(15,23,42,.2)" }}>
              {filteredAirportOptions.length ? filteredAirportOptions.map((option) => {
                const selected = option.key === airportKey && String(option.terminal || "") === String(form.terminal || "");
                return <button key={`${option.key}-${option.terminal || "airport"}`} type="button" role="option" aria-selected={selected} onMouseDown={(event) => event.preventDefault()} onClick={() => chooseAirportOption(option)} style={{ display: "flex", width: "100%", alignItems: "center", gap: 9, padding: "11px 12px", border: 0, borderTop: "1px solid #edf2f7", background: selected ? "#eaf2ff" : "#fff", color: "#0f2f63", textAlign: "left", fontWeight: selected ? 900 : 750, cursor: "pointer" }}><span style={{ width: 16, color: selected ? "#1264ff" : "#94a3b8" }}>{selected ? "✓" : "✈"}</span>{option.label}</button>;
              }) : <div style={{ padding: 12, color: "#64748b", fontSize: 12 }}>No matching airport or terminal</div>}
            </div>
          ) : null}
        </label>
        <label style={{ position: "relative", display: "block", order: 3 }}>
          <span style={labelStyle}>{isReturn ? "Arriving flight (optional)" : "Departing flight (optional)"}</span>
          <input
            value={form.flight}
            onChange={(event) => {
              onChange("flight", event.target.value.toUpperCase());
              if (form.manual_local) setManualFlightTime(true);
              else if (!manualFlightTime) { setSelectedFlightNumber(""); onChange("date", ""); onChange("flight_anchor_utc", ""); }
            }}
            onFocus={() => setFlightFocused(true)}
            onBlur={() => setTimeout(() => setFlightFocused(false), 120)}
            autoComplete="off"
            placeholder={selectedAirport?.key === "heathrow" ? "e.g. BA123" : "e.g. FR669"}
            className="ajp-mobile-field"
            style={compactInputStyle}
          />
          {flightFocused && flightSuggestions.length ? (
            <div role="listbox" style={{ position: "absolute", zIndex: 20, top: "calc(100% + 5px)", left: 0, right: 0, borderRadius: 10, border: "1px solid #c9d8ef", background: "#fff", boxShadow: "0 14px 30px rgba(15,23,42,.18)", overflow: "hidden" }}>
              {flightSuggestions.map((flight) => (
                <button key={flight} type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => { onChange("flight", flight); setSelectedFlightNumber(flight); setManualFlightTime(false); setManualTimeError(""); onChange("manual_local", ""); onChange("date", ""); onChange("flight_anchor_utc", ""); setFlightSuggestions([]); setFlightFocused(false); trackFunnelEvent("ajp_flight_found", { brand: "planner", tracking_source: "ajp_home", airport: selectedAirport?.code, flight_number: flight, direction: isReturn ? "from_airport" : "to_airport", status: "selected" }); }} style={{ display: "block", width: "100%", padding: "10px 12px", border: 0, borderTop: "1px solid #eef2f7", background: "#fff", color: "#0f2f63", textAlign: "left", fontWeight: 800, cursor: "pointer" }}>{flight}</button>
              ))}
            </div>
          ) : null}
        </label>
        <label className="ajp-date-field" style={{ position: "relative", display: "block", order: 4 }}>
          <span style={labelStyle}>{isReturn ? "Arrival date & time" : "Departure date & time"}</span>
          {supportsScheduledFlights && !useManualFlightTime ? (
            <select className="ajp-mobile-field" value={form.flight_anchor_utc || ""} onChange={(event) => { if (event.target.value === "__manual__") { setManualFlightTime(true); onChange("flight_anchor_utc", ""); onChange("date", ""); return; } const row = flightSchedules.find((item) => scheduleAnchor(item) === event.target.value); setManualFlightTime(false); onChange("manual_local", ""); onChange("flight_anchor_utc", event.target.value); onChange("date", row ? scheduleDate(row) : ""); const detectedTerminal = row ? scheduleTerminal(row) : ""; if (detectedTerminal) onChange("terminal", detectedTerminal); }} style={{ ...compactInputStyle, appearance: "none", cursor: "pointer" }} disabled={scheduleLoading}>
              <option value="">{scheduleLoading ? "Finding flight times…" : flightSchedules.length ? `Select ${isReturn ? "arrival" : "departure"} time` : scheduleLookupFlight ? `No future ${selectedAirport.code} ${isReturn ? "arrival" : "departure"} found` : form.flight ? "Enter the full flight number or choose a match above" : isReturn ? "Choose flight or enter time manually" : "Select flight first"}</option>
              {flightSchedules.map((row) => <option key={scheduleAnchor(row)} value={scheduleAnchor(row)} style={{ color: "#071948" }}>{scheduleLabel(row)}</option>)}
              <option value="__manual__" style={{ color: "#071948" }}>Enter date and time manually…</option>
            </select>
          ) : supportsScheduledFlights && useManualFlightTime ? (
            <div style={{ display: "grid", gridTemplateColumns: form.flight ? "1fr 38px" : "1fr", gap: 6 }}>
              <input className="ajp-mobile-field" type="datetime-local" min={minimumManualLocal} aria-describedby={manualTimeError ? "ajp-manual-time-error" : undefined} value={form.manual_local || ""} onChange={(event) => { const local = event.target.value; const anchor = londonLocalToUtcIso(local); setManualTimeError(anchor && new Date(anchor).getTime() <= Date.now() ? "Past times can’t be used. Choose a future date and time." : ""); onChange("manual_local", local); onChange("date", local ? local.slice(0, 10) : ""); onChange("flight_anchor_utc", anchor); }} onBlur={(event) => { const local = event.currentTarget.value; const anchor = londonLocalToUtcIso(local); if (anchor && new Date(anchor).getTime() <= Date.now()) { onChange("manual_local", ""); onChange("date", ""); onChange("flight_anchor_utc", ""); setManualTimeError("Past times can’t be used. Choose a future date and time."); return; } if (local) trackFunnelEvent("ajp_flight_manually_entered", { brand: "planner", tracking_source: "ajp_home", airport: selectedAirport?.code, flight_number: form.flight || undefined, direction: isReturn ? "from_airport" : "to_airport", manual_time: true }); }} style={{ ...compactInputStyle, colorScheme: "light", minWidth: 0 }} />
              {form.flight ? <button className="ajp-time-toggle" type="button" title="Use scheduled flight times" aria-label="Use scheduled flight times" onClick={() => { setManualFlightTime(false); onChange("manual_local", ""); onChange("date", ""); onChange("flight_anchor_utc", ""); }} style={{ border: "1px solid #d7e1ef", borderRadius: 10, background: "#ffffff", color: "#17457f", cursor: "pointer", fontWeight: 900 }}>↺</button> : null}
            </div>
          ) : (
            <input className="ajp-mobile-field" type="date" min={minimumManualLocal.slice(0, 10)} value={form.date || ""} onChange={(event) => onChange("date", event.target.value)} style={{ ...compactInputStyle, colorScheme: "light" }} />
          )}
          {manualTimeError ? <small id="ajp-manual-time-error" role="alert" style={{ display: "block", marginTop: 4, color: "#fecaca", fontSize: 10, fontWeight: 750, lineHeight: 1.25 }}>{manualTimeError}</small> : null}
        </label>
        {!isCompact && (
          <button
            type="button"
            onClick={() => onSubmit(airportKey)}
            disabled={loading}
            style={{
              order: 5,
              minHeight: 60,
              border: "none",
              borderRadius: 10,
              background: "linear-gradient(135deg, #1264ff 0%, #064de7 100%)",
              color: "#ffffff",
              fontSize: 15,
              fontWeight: 900,
              cursor: loading ? "wait" : "pointer",
              opacity: loading ? 0.75 : 1,
              boxShadow: "0 18px 34px rgba(37, 99, 235, 0.34)",
            }}
          >
            {loading ? "Building live plan…" : "Build my live plan →"}
          </button>
        )}
      </div>

      {isCompact && (
        <button
          className="ajp-build-cta"
          type="button"
          onClick={() => onSubmit(airportKey)}
          disabled={loading}
          style={{
            marginTop: isNarrow ? 8 : 10,
            width: isCompact ? "100%" : "72%",
            minHeight: isNarrow ? 46 : 52,
            border: "none",
            borderRadius: 10,
            background: "linear-gradient(135deg, #1264ff 0%, #064de7 100%)",
            color: "#ffffff",
            fontSize: isNarrow ? 14 : 15,
            fontWeight: 900,
            cursor: loading ? "wait" : "pointer",
            opacity: loading ? 0.75 : 1,
            boxShadow: "0 18px 34px rgba(37, 99, 235, 0.34)",
          }}
        >
          {loading ? "Building live plan…" : "Build my live plan →"}
        </button>
      )}

      {(addressResolutionNote || form.flight) ? <div style={{ marginTop: isNarrow ? 5 : 7, minHeight: 14, display: "flex", alignItems: "center", justifyContent: "space-between", gap: isNarrow ? 6 : 12, color: "#cfe1ff", fontSize: isNarrow ? 9 : 10, lineHeight: 1.3, fontWeight: 750 }}>
        <span style={{ color: addressResolutionNote.startsWith("City-centre") ? "#fde68a" : "#fef3c7" }}>{addressResolutionNote}</span>
        {form.flight ? (manualFlightTime ? (
          <button className="ajp-manual-time-link" type="button" onClick={() => { setManualFlightTime(false); onChange("manual_local", ""); onChange("date", ""); onChange("flight_anchor_utc", ""); }} style={{ marginLeft: "auto", padding: 0, border: 0, background: "transparent", color: "#cfe1ff", textDecoration: "underline", font: "inherit", lineHeight: 1.3, cursor: "pointer" }}>Use times found for {form.flight}</button>
        ) : (
          <button className="ajp-manual-time-link" type="button" onClick={() => { setManualFlightTime(true); onChange("date", ""); onChange("flight_anchor_utc", ""); }} style={{ marginLeft: "auto", maxWidth: isNarrow ? 250 : "none", padding: 0, border: 0, background: "transparent", color: "#cfe1ff", textDecoration: "underline", font: "inherit", lineHeight: 1.3, cursor: "pointer" }}>Can’t find the right flight? Enter the {isReturn ? "arrival" : "departure"} time manually</button>
        )) : null}
      </div> : null}
      {error ? <div role="alert" style={{ marginTop: 10, padding: "9px 11px", borderRadius: 9, background: "rgba(127,29,29,.72)", border: "1px solid rgba(254,202,202,.35)", color: "#fee2e2", fontSize: 12, fontWeight: 750 }}>{error}</div> : null}
      <div className="ajp-home-alert" style={{ marginTop: isNarrow ? 5 : 8, display: "flex", justifyContent: "flex-end" }}><JourneyAlertConversion compact triggerStyle={isNarrow ? { minHeight: 36, marginTop: 2, padding: "5px 8px", fontSize: 9 } : undefined} airportCode={selectedAirport.code} postcode={form.postcode} flightNumber={form.flight} flightAnchorUtc={form.flight_anchor_utc || form.manual_local || (form.date ? `${form.date}T12:00` : "")} direction={form.direction === "return" ? "from_airport" : "to_airport"} placement="homepage-hero" label="Get journey alerts →" /></div>
    </section>
  );
}

const formatPlanTime = (value) => {
  if (!value) return "—:—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—:—";
  return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Europe/London" });
};

const formatPlanDateTime = (value) => {
  if (!value) return "Time not confirmed";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Time not confirmed";
  return date.toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Europe/London" });
};

const hasPlanNumber = (value) => value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value));

const classifyAjpFailure = (error) => {
  const message = String(error?.response?.data?.error || error?.message || "").toLowerCase();
  const status = Number(error?.response?.status) || undefined;
  if (error?.code === "ECONNABORTED" || message.includes("timeout")) return { error_code: "timeout", error_stage: "plan_request", http_status: status };
  if (message.includes("postcode") || message.includes("full uk address") || message.includes("location")) return { error_code: "location_resolution_failed", error_stage: "location_resolution", http_status: status };
  if (message.includes("flight") && (message.includes("not found") || message.includes("no future"))) return { error_code: "flight_not_found", error_stage: "flight_validation", http_status: status };
  if (message.includes("time") || message.includes("date")) return { error_code: "invalid_flight_time", error_stage: "time_validation", http_status: status };
  if (status === 429) return { error_code: "rate_limited", error_stage: "plan_request", http_status: status };
  if (status && status >= 500) return { error_code: "server_error", error_stage: "plan_request", http_status: status };
  return { error_code: error?.response?.data?.code || error?.code || "plan_request_failed", error_stage: "plan_request", http_status: status };
};

const formatJourneyDuration = (value) => {
  if (!hasPlanNumber(value)) return "Live route checked";
  const totalMins = Math.max(0, Math.round(Number(value)));
  if (totalMins < 60) return `${totalMins} min`;
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  return mins ? `${hours} hr ${mins} min` : `${hours} hr`;
};

const airportTransferLabel = (airport, terminal) => {
  const cleanTerminal = String(terminal || "").replace(/^terminal\s*/i, "").trim();
  return `${airport?.name || airport?.code || "Airport"}${cleanTerminal ? ` Terminal ${cleanTerminal}` : " Airport"}`;
};

const buildAjpDropoffUrl = ({ path, airport, terminal, dropoffAt, flightNumber, flightAt, direction, journeyId }) => {
  if (!path) return null;
  const url = new URL(path, typeof window !== "undefined" ? window.location.origin : "https://www.airportjourneyplanner.com");
  Object.entries({
    airport_code: airport?.code,
    terminal,
    dropoff_at: dropoffAt,
    flight_number: flightNumber,
    flight_time: flightAt,
    direction,
    ajp_journey_id: journeyId,
    utm_source: "airportjourneyplanner",
    utm_medium: "referral",
    utm_campaign: "dropoff_calculator_handoff",
  }).forEach(([key, value]) => {
    if (value !== null && value !== undefined && String(value).trim()) url.searchParams.set(key, String(value));
  });
  return url.toString();
};

const toLocalDateTimeInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 16);
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
};

const AJP_STATION_LABELS = {
  BHM: "Birmingham New Street", BMH: "Bournemouth", BNM: "Burnham (Berks)", COV: "Coventry", CTR: "Chester",
  DON: "Doncaster", EUS: "London Euston", HAF: "Heathrow Terminal 4", HAY: "Hayes & Harlington",
  HWF: "Heathrow Terminal 4", HWV: "Heathrow Terminal 5", HWX: "Heathrow Terminal 5",
  HXX: "Heathrow Terminals 2 & 3", LEI: "Leicester", LHR: "Heathrow Central", LTN: "Luton Airport Parkway",
  MAN: "Manchester Piccadilly", MIA: "Manchester Airport", MKC: "Milton Keynes Central", EMD: "East Midlands Parkway",
  GTW: "Gatwick Airport", PAD: "London Paddington", POO: "Poole", RDG: "Reading", SLO: "Slough",
  SOU: "Southampton Central", SSD: "Stansted Airport", STN: "Stansted Airport", TCR: "Tottenham Court Road",
  TOM: "Tottenham Hale", WOK: "Woking", ZFD: "Farringdon",
};

const normalizeAjpStationLabel = (value, fallback = "Station") => {
  if (!value) return fallback;
  const raw = typeof value === "string" ? value : value.name || value.station_name || value.display_name || value.crs || value.code || "";
  const cleanRaw = String(raw).trim();
  const code = cleanRaw.toUpperCase();
  const label = AJP_STATION_LABELS[code] || cleanRaw;
  return String(label).replace(/\s+Rail Station$/i, "").replace(/\s+Station$/i, "").trim() || fallback;
};

const openAjpEnquiryChat = ({ vehiclePreference, pickup, dropoff, pickupAt, flightNumber, flightAt, airport, terminal, direction, journeyId, passengers, bags }) => {
  if (typeof window === "undefined") return;
  const isMinibus = vehiclePreference === "Minibus";
  window.dispatchEvent(new CustomEvent("ccg:open-support-chat", {
    detail: {
      flow: "enquiry",
      variant: "ajp_minibus",
      source: "ajp_minibus_cta",
      prefill: {
        vehicle_preference: vehiclePreference || "Not sure",
        passengers: String(passengers ?? (isMinibus ? 8 : 4)),
        bags: String(bags ?? (isMinibus ? 4 : 2)),
        pickup_location: pickup || "",
        dropoff_location: dropoff || "",
        pickup_datetime: toLocalDateTimeInput(pickupAt),
        flight_number: flightNumber && flightNumber !== "Flight" ? flightNumber : "",
        flight_time: toLocalDateTimeInput(flightAt),
        airport_code: airport?.code || "",
        terminal: terminal || "",
        direction: direction || "",
        ajp_journey_id: journeyId || "",
      },
    },
  }));
};

const cleanFlightPlace = (...values) => values.find((value) => value != null && String(value).trim()) || null;

function AjpFlightStrip({ flightNumber, airline, originCode, originName, departureAt, originTerminal, originGate, originZone, destinationCode, destinationName, arrivalAt, destinationTerminal, destinationGate, destinationZone, isCompact }) {
  if (!flightNumber) return null;
  const origin = cleanFlightPlace(originName, originCode, "Origin");
  const destination = cleanFlightPlace(destinationName, destinationCode, "Destination");
  const compactTerminal = (value) => {
    if (!value) return null;
    const clean = String(value).replace(/^terminal\s*/i, "").trim();
    if (/^\d+$/.test(clean)) return `T${clean}`;
    if (/^(north|south)$/i.test(clean)) return `${clean[0].toUpperCase()}${clean.slice(1).toLowerCase()} Terminal`;
    return clean;
  };
  const formatGate = (value) => value ? `Gate ${String(value).replace(/^gate\s*/i, "")}` : null;
  const formatZone = (value) => value ? `Zone ${String(value).replace(/^zone\s*/i, "")}` : null;
  const originDetail = [origin, originCode && originCode !== origin ? originCode : null, compactTerminal(originTerminal), formatGate(originGate), formatZone(originZone), departureAt ? formatPlanDateTime(departureAt) : null].filter(Boolean).join(" · ");
  const destinationDetail = [destination, destinationCode && destinationCode !== destination ? destinationCode : null, compactTerminal(destinationTerminal), formatGate(destinationGate), formatZone(destinationZone), arrivalAt ? formatPlanDateTime(arrivalAt) : null].filter(Boolean).join(" · ");
  return (
    <div style={{ marginTop: 8, minHeight: 38, padding: isCompact ? "7px 10px" : "6px 12px", borderRadius: 9, background: "linear-gradient(135deg,#071f52,#0d347a)", color: "#fff", display: "flex", alignItems: "center", gap: isCompact ? 7 : 12, flexWrap: isCompact ? "wrap" : "nowrap" }}>
      <strong style={{ flex: "0 0 auto", fontSize: 12, whiteSpace: "nowrap" }}>✈ {[airline, flightNumber].filter(Boolean).join(" · ")}</strong>
      <span aria-hidden="true" style={{ width: 1, alignSelf: "stretch", background: "rgba(169,200,248,.3)" }} />
      <span title={`${originDetail} → ${destinationDetail}`} style={{ minWidth: 0, flex: "1 1 auto", color: "#d8e7ff", fontSize: 11, lineHeight: 1.35, fontWeight: 750, whiteSpace: isCompact ? "normal" : "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}><b style={{ color: "#fff" }}>{originDetail}</b> <span style={{ color: "#8fb8f3", padding: "0 4px" }}>→</span> <b style={{ color: "#fff" }}>{destinationDetail}</b></span>
      {!isCompact ? <small style={{ flex: "0 0 auto", color: "#a9c8f8", fontSize: 9, fontWeight: 800, whiteSpace: "nowrap" }}>Published schedule</small> : null}
    </div>
  );
}

function ReturnJourneyPreview({ airport, form, planResponse, onViewDetails }) {
  const isCompact = useCompactMobile(820);
  const [detailMode, setDetailMode] = useState("");
  const [activeMobileMode, setActiveMobileMode] = useState("taxi");
  const plan = planResponse?.data || {};
  const projection = planResponse?.ui_projection || plan?.ui_projection || {};
  const cards = projection?.cards || {};
  const flight = plan?.flight || {};
  const showFlightStrip = Boolean(String(form?.flight || "").trim()) && !form?.manual_local && String(flight?.number || form?.flight || "").toUpperCase() !== "MANUAL";
  const guidance = plan?.return_guidance || {};
  const arrivalAt = flight.arrival || flight.scheduled_arrival || form.flight_anchor_utc;
  const exitAt = guidance.terminal_exit_at || guidance.ready_to_leave_at;
  const recommendedMode = projection?.recommended_mode || projection?.overall?.recommended_mode || plan?.recommendation?.recommended_mode || "taxi";
  const recommendedCard = cards[recommendedMode] || cards.taxi || {};
  const homeAt = exitAt && hasPlanNumber(recommendedCard.duration_mins)
    ? new Date(new Date(exitAt).getTime() + Number(recommendedCard.duration_mins) * 60000).toISOString()
    : null;
  const terminal = flight.terminal ? (/^terminal/i.test(String(flight.terminal)) ? flight.terminal : `Terminal ${flight.terminal}`) : "Terminal to be confirmed";
  const terminalNumber = String(flight.terminal || form.terminal || "").replace(/\D/g, "");
  const airportLocation = airportTransferLabel(airport, terminalNumber);
  const customerLocation = form.pickup_address || form.postcode;
  const railMode = plan?.modes?.rail || {};
  const railJourney = railMode?.best_candidate?.journey || railMode?.best_candidate?.journeys?.[0] || cards?.rail?.best_candidate?.journey || cards?.rail?.best_candidate?.journeys?.[0] || null;
  const railLegs = Array.isArray(railJourney?.legs) ? railJourney.legs : [];
  const railSegments = Array.isArray(railMode?.segments) ? railMode.segments : [];
  const selfDriveMode = plan?.modes?.self_drive || {};
  const roadSteps = Array.isArray(selfDriveMode?.road?.route_detail?.steps) ? selfDriveMode.road.route_detail.steps : [];
  const legPoint = (leg, side) => leg?.[`${side}_name`] || leg?.[`${side}_crs`] || leg?.[side] || "";
  const railRoutePoints = railLegs.length
    ? [legPoint(railLegs[0], "from"), ...railLegs.map((leg) => legPoint(leg, "to"))].filter(Boolean)
    : [];
  const normalizedRailRoutePoints = railRoutePoints.map((point) => normalizeAjpStationLabel(point, point)).filter((point, index, list) => index === 0 || point !== list[index - 1]);
  const lastMileSegment = railSegments.find((segment) => segment?.id === "last_mile");
  const returnRailRoutePoints = lastMileSegment && customerLocation && normalizedRailRoutePoints[normalizedRailRoutePoints.length - 1] !== customerLocation
    ? [...normalizedRailRoutePoints, customerLocation]
    : normalizedRailRoutePoints;
  const railRouteLabel = returnRailRoutePoints.length ? returnRailRoutePoints.join(" → ") : "Airport station → destination station → home";
  const roadNames = [...new Set(roadSteps.flatMap((step) => String(`${step?.name || ""} ${step?.instruction || ""}`).toUpperCase().match(/\b(?:M\d{1,3}[A-Z]?|A\d{1,4}[A-Z]?)\b/g) || []))];
  const roadRouteLabel = roadNames.length
    ? `${airport.name} → ${roadNames.slice(0, 5).join(" → ")} → ${customerLocation || "home"}`
    : `${airport.name} → ${customerLocation || "home"}`;
  const modes = [
    { key: "taxi", icon: "🚕", title: "Taxi & minibus", image: "/ajp-minibus-card.webp", detail: "Direct from arrivals to your door", fallback: "Door-to-door after baggage collection" },
    { key: "rail", icon: "🚆", title: "Rail", image: "/ajp-rail-card.webp", detail: "Airport station to your destination", fallback: "Rail route with station connections" },
    { key: "self_drive", icon: "🚙", title: "Collect your car", image: "/ajp-parking-card.webp", detail: "Terminal transfer, then drive home", fallback: "Collect your parked car and drive home" },
  ];
  useEffect(() => {
    setActiveMobileMode("taxi");
  }, [planResponse, airport?.code, form?.direction]);
  const openEnquiry = (flow) => {
    const kind = flow.startsWith("minibus") ? "minibus" : "taxi";
    trackFunnelEvent(kind === "minibus" ? "ajp_minibus_cta_click" : "ajp_taxi_cta_click", { brand: "planner", tracking_source: "ajp_home", airport: airport.code, flow, direction: "from_airport", ajp_journey_id: plan?.journey_id });
    if (typeof window === "undefined") return;
    openAjpEnquiryChat({ vehiclePreference: kind === "minibus" ? "Minibus" : "Taxi", pickup: airportLocation, dropoff: customerLocation, pickupAt: exitAt, flightNumber: form.flight, flightAt: arrivalAt, airport, terminal: terminalNumber, direction: "from_airport", journeyId: plan?.journey_id });
  };
  const openReturnDetails = (mode) => {
    setDetailMode(mode);
    trackFunnelEvent("ajp_return_detail_open", { brand: "planner", tracking_source: "ajp_home", airport: airport.code, mode, direction: "from_airport", ajp_journey_id: plan?.journey_id });
  };
  useEffect(() => {
    if (!detailMode || typeof window === "undefined") return undefined;
    const onKeyDown = (event) => { if (event.key === "Escape") setDetailMode(""); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [detailMode]);
  return (
    <section aria-label="Journey plan preview" style={{ marginTop: 14, border: "1px solid #dce5f0", borderRadius: 18, background: "#fff", boxShadow: "0 18px 42px rgba(15,35,70,.12)", overflow: "hidden" }}>
      <div style={{ padding: isCompact ? 14 : "17px 20px", background: "linear-gradient(135deg,#eaf3ff,#f8fbff)", borderBottom: "1px solid #dce5f0" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}><strong style={{ color: "#0b2b64", fontSize: 16 }}>Your journey home from {airport.name}</strong><span style={{ color: "#15803d", fontSize: 12, fontWeight: 850 }}>● Return journey checked</span></div>
        {showFlightStrip ? <AjpFlightStrip flightNumber={flight.number || form.flight} airline={flight.airline?.name || flight.airline?.code} originCode={flight.origin?.airport_code} originName={flight.origin?.airport_name || flight.origin?.city_name} departureAt={flight.departure || flight.scheduled_departure} destinationCode={airport.code} destinationName={airport.name} arrivalAt={arrivalAt} destinationTerminal={terminal} destinationGate={flight.gate_number || flight.gate} destinationZone={flight.zone || flight.arrival_zone} isCompact={isCompact} /> : null}
        <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: isCompact ? 6 : 9 }}>
          {[{ icon: "✈", label: "Flight arrives", at: arrivalAt }, { icon: "🧳", label: "Ready to leave", at: exitAt }, { icon: "⌂", label: "Estimated home", at: homeAt }].map((step) => <div key={step.label} style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "36px 1fr", gap: isCompact ? 4 : 9, justifyItems: isCompact ? "center" : "stretch", padding: isCompact ? "9px 5px" : 11, border: "1px solid #c8dcf5", borderRadius: 12, background: "#eef6ff", textAlign: isCompact ? "center" : "left" }}><span style={{ width: isCompact ? 30 : 34, height: isCompact ? 30 : 34, display: "grid", placeItems: "center", borderRadius: 99, background: "#d8e9ff" }}>{step.icon}</span><span><small style={{ color: "#64748b", fontSize: isCompact ? 9 : 11 }}>{step.label}</small><b style={{ display: "block", color: "#0f2f63", fontSize: isCompact ? 15 : 16 }}>{formatPlanTime(step.at)}</b></span></div>)}
        </div>
      </div>
      <div style={{ padding: isCompact ? 14 : 18 }}>
        <strong style={{ color: "#0b2b64", fontSize: 17 }}>Compare three ways home</strong><div style={{ marginTop: 3, color: "#64748b", fontSize: 12 }}>{isCompact ? "Tap an option to see its full journey, time and cost." : "Times start when you are expected to be ready outside the terminal."}</div>
        {isCompact ? <div role="tablist" aria-label="Choose a journey home option" style={{ marginTop: 12, display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 7 }}>
          {modes.map((mode) => {
            const card = cards[mode.key] || {};
            const selected = activeMobileMode === mode.key;
            const recommended = recommendedMode === mode.key || card.recommended;
            return <button key={mode.key} type="button" role="tab" aria-selected={selected} onClick={() => setActiveMobileMode(mode.key)} style={{ minWidth: 0, minHeight: 76, padding: "9px 6px", borderRadius: 12, border: selected ? "2px solid #1264ed" : "1px solid #c8dcf5", background: selected ? "#dbeafe" : "#eef6ff", color: "#0b2b64", boxShadow: selected ? "0 8px 18px rgba(18,100,237,.14)" : "none", cursor: "pointer", textAlign: "left" }}>
              <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}><span aria-hidden="true" style={{ fontSize: 18 }}>{mode.icon}</span>{recommended ? <small style={{ padding: "3px 5px", borderRadius: 99, background: "#173f7a", color: "#fff", fontSize: 7, fontWeight: 950, letterSpacing: ".03em" }}>RECOMMENDED</small> : null}</span>
              <strong style={{ display: "block", marginTop: 5, fontSize: 11, lineHeight: 1.15 }}>{mode.title}</strong>
              <small style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", minHeight: 14, marginTop: 4, color: selected ? "#1254c4" : "#526783", fontSize: 8, lineHeight: 1, fontWeight: 900 }}>{selected ? "Viewing" : <span aria-hidden="true" style={{ fontSize: 15 }}>›</span>}</small>
            </button>;
          })}
        </div> : null}
        <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(3,minmax(0,1fr))", gap: 12 }}>
          {modes.map((mode) => {
            const card = cards[mode.key] || {};
            const recommended = recommendedMode === mode.key || card.recommended;
            const style = AJP_MODE_STYLES[mode.key === "rail" ? "train" : mode.key];
            const costValue = card.cost_gbp ?? card.fare_gbp;
            const route = mode.key === "rail" ? railRouteLabel : mode.key === "self_drive" ? roadRouteLabel : card.route_label || card.via || card.summary || card.insight || mode.fallback;
            if (isCompact && activeMobileMode !== mode.key) return null;
            return <article key={mode.key} role={isCompact ? "tabpanel" : undefined} style={{ display: "flex", flexDirection: "column", padding: 14, borderRadius: 14, background: isCompact ? "linear-gradient(145deg,#f2f7ff,#e7f1ff)" : style.background, border: `${recommended ? 2 : 1}px solid ${recommended ? "#173f7a" : style.border}`, boxShadow: "0 8px 20px rgba(15,47,99,.08)", overflow: "hidden" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><strong style={{ color: "#0b2b64", fontSize: 17 }}>{mode.icon} {mode.title}</strong>{recommended ? <span style={{ padding: "4px 8px", borderRadius: 99, background: "#173f7a", color: "#fff", fontSize: 9, fontWeight: 900 }}>RECOMMENDED</span> : null}</div>
              <div aria-hidden="true" style={{ height: 64, margin: "9px 0 2px", borderRadius: 10, border: `1px solid ${style.border}`, backgroundImage: `linear-gradient(90deg,rgba(255,255,255,.12),rgba(255,255,255,0)),url('${mode.image}')`, backgroundSize: "cover", backgroundPosition: "center 52%", boxShadow: "inset 0 -18px 24px rgba(7,31,82,.08)" }} />
              <div style={{ margin: "14px 0", display: "grid", gap: 9 }}><div><small style={{ color: "#64748b" }}>Estimated price</small><b style={{ display: "block", color: "#0f2f63", fontSize: 19 }}>{hasPlanNumber(costValue) ? `£${Number(costValue).toFixed(2)}` : "Check options"}</b></div><div><small style={{ color: "#64748b" }}>Journey time</small><b style={{ display: "block", color: "#0f2f63" }}>{formatJourneyDuration(card.duration_mins)}</b></div><div style={{ color: "#52657f", fontSize: 11, lineHeight: 1.4 }}>{route}</div></div>
              {mode.key === "taxi" ? <div style={{ marginTop: "auto", display: "grid", gridTemplateColumns: "1.25fr .85fr", gap: 7 }}><button type="button" onClick={() => openEnquiry("minibus-enquiry")} style={{ minHeight: 42, border: 0, borderRadius: 9, background: style.accent, color: "#fff", fontWeight: 900, cursor: "pointer" }}>Get minibus quote</button><button type="button" onClick={() => openEnquiry("taxi-enquiry")} style={{ minHeight: 42, border: `1px solid ${style.accent}`, borderRadius: 9, background: "#fff", color: style.accent, fontWeight: 850, cursor: "pointer" }}>Taxi</button></div> : <button type="button" onClick={() => openReturnDetails(mode.key)} style={{ marginTop: "auto", minHeight: 42, border: 0, borderRadius: 9, background: style.accent, color: "#fff", fontWeight: 900, cursor: "pointer" }}>{mode.key === "rail" ? "View rail journey" : "View collection plan"}</button>}
              <small style={{ marginTop: 8, color: "#64748b", textAlign: "center" }}>{mode.detail}</small>
            </article>;
          })}
        </div>
      </div>
      {detailMode ? <div role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setDetailMode(""); }} style={{ position: "fixed", inset: 0, zIndex: 10020, display: "grid", placeItems: "center", padding: 16, background: "rgba(3,16,43,.66)", backdropFilter: "blur(3px)" }}>
        <section role="dialog" aria-modal="true" aria-label={detailMode === "rail" ? "Return rail journey" : "Return collection plan"} style={{ width: "min(720px,100%)", maxHeight: "min(760px,90vh)", overflowY: "auto", borderRadius: 18, background: "#fff", boxShadow: "0 24px 70px rgba(0,0,0,.32)" }}>
          <header style={{ position: "sticky", top: 0, zIndex: 1, display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", padding: "16px 18px", color: "#fff", background: detailMode === "rail" ? "#17833f" : "#1d4ed8" }}><div><small style={{ display: "block", opacity: .82, fontWeight: 800 }}>Your return plan</small><strong style={{ fontSize: 20 }}>{detailMode === "rail" ? "Rail journey home" : "Collect your car and drive home"}</strong></div><button type="button" onClick={() => setDetailMode("")} aria-label="Close return plan" style={{ width: 36, height: 36, border: "1px solid rgba(255,255,255,.55)", borderRadius: 99, background: "rgba(255,255,255,.14)", color: "#fff", fontSize: 22, cursor: "pointer" }}>×</button></header>
          <div style={{ padding: 18, color: "#183153" }}>
            {detailMode === "rail" ? <>
              <div style={{ padding: 13, borderRadius: 12, background: "#edf9f1", color: "#14532d", fontWeight: 850, lineHeight: 1.5 }}>{railRouteLabel}</div>
              <div style={{ marginTop: 14, display: "grid", gap: 9 }}>
                {railSegments.filter((segment) => segment?.id !== "rail").map((segment) => <div key={segment.id} style={{ padding: 12, border: "1px solid #d8e7dd", borderRadius: 11 }}><strong>{segment.id === "terminal_transfer" ? "Terminal to station" : "Final mile home"}</strong><div style={{ marginTop: 3, color: "#52657f", fontSize: 13 }}>{normalizeAjpStationLabel(segment.from, segment.from)} → {segment.id === "last_mile" && customerLocation ? customerLocation : normalizeAjpStationLabel(segment.to, segment.to)}{hasPlanNumber(segment.eta_safe_mins) ? ` · ${formatJourneyDuration(segment.eta_safe_mins)}` : ""}{segment.mode ? ` · ${String(segment.mode).replace(/_/g, " ")}` : ""}</div></div>)}
                {railLegs.map((leg, index) => <div key={`${index}-${legPoint(leg, "from")}-${legPoint(leg, "to")}`} style={{ display: "grid", gridTemplateColumns: "52px 1fr", gap: 10, padding: 12, border: "1px solid #d8e7dd", borderRadius: 11 }}><b style={{ color: "#17833f" }}>{formatPlanTime(leg.sched_dep || leg.depart || leg.dep)}</b><div><strong>{normalizeAjpStationLabel(legPoint(leg, "from"), "Station")} → {normalizeAjpStationLabel(legPoint(leg, "to"), "Station")}</strong><div style={{ marginTop: 3, color: "#64748b", fontSize: 12 }}>{leg.operator || leg.operator_name || "Rail service"}{leg.platform_dep ? ` · Platform ${leg.platform_dep}` : ""}{(leg.sched_arr || leg.arrive || leg.arr) ? ` · arrives ${formatPlanTime(leg.sched_arr || leg.arrive || leg.arr)}` : ""}</div></div></div>)}
                {!railLegs.length ? <div style={{ padding: 12, border: "1px solid #fde68a", borderRadius: 11, background: "#fffbeb" }}>Detailed rail legs are not available for this result. The route summary and total journey time remain valid.</div> : null}
              </div>
            </> : <>
              <div style={{ padding: 13, borderRadius: 12, background: "#eef4ff", color: "#153e91", fontWeight: 850, lineHeight: 1.5 }}>{roadRouteLabel}</div>
              {selfDriveMode?.terminal_to_car_park ? <div style={{ marginTop: 14, padding: 12, border: "1px solid #d7e3fa", borderRadius: 11 }}><strong>1. Leave the terminal and collect your car</strong><div style={{ marginTop: 3, color: "#52657f", fontSize: 13 }}>{selfDriveMode.terminal_to_car_park.mode ? String(selfDriveMode.terminal_to_car_park.mode).replace(/_/g, " ") : "Walk or shuttle"}{hasPlanNumber(selfDriveMode.terminal_to_car_park.duration_mins) ? ` · ${formatJourneyDuration(selfDriveMode.terminal_to_car_park.duration_mins)}` : ""}</div></div> : null}
              <div style={{ marginTop: 9, padding: 12, border: "1px solid #d7e3fa", borderRadius: 11 }}><strong>2. Drive home</strong><div style={{ marginTop: 3, color: "#52657f", fontSize: 13 }}>{formatJourneyDuration(cards?.self_drive?.duration_mins)} · estimated cost {hasPlanNumber(cards?.self_drive?.cost_gbp) ? `£${Number(cards.self_drive.cost_gbp).toFixed(2)}` : "not available"}</div></div>
              {roadSteps.length ? <div style={{ marginTop: 14, display: "grid", gap: 8 }}>{roadSteps.slice(0, 12).map((step, index) => <div key={index} style={{ padding: "9px 11px", borderLeft: "3px solid #1d4ed8", background: "#f8fbff", borderRadius: "0 8px 8px 0", color: "#40536e", fontSize: 13 }}>{step.instruction || step.name || "Continue on the planned route"}</div>)}</div> : <div style={{ marginTop: 14, padding: 12, border: "1px solid #d7e3fa", borderRadius: 11, background: "#f8fbff" }}>The calculated road duration and cost are available, but turn-by-turn road names were not returned for this route.</div>}
            </>}
            <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}><button type="button" onClick={onViewDetails} style={{ minHeight: 40, padding: "0 16px", border: "1px solid #b8c9e4", borderRadius: 9, background: "#fff", color: "#17427f", fontWeight: 850, cursor: "pointer" }}>Open full plan in new tab ↗</button><button type="button" onClick={() => setDetailMode("")} style={{ minHeight: 40, padding: "0 18px", border: 0, borderRadius: 9, background: "#0b2b64", color: "#fff", fontWeight: 900, cursor: "pointer" }}>Done</button></div>
          </div>
        </section>
      </div> : null}
    </section>
  );
}

function QuickJourneyPreview({ airport, form, planResponse, loading, onOpenPlan, onViewDetails }) {
  const isCompact = useCompactMobile(820);
  const [activeMobileMode, setActiveMobileMode] = useState("taxi");
  const projection = planResponse?.ui_projection || planResponse?.data?.ui_projection || null;
  const plan = planResponse?.data || null;
  const cards = projection?.cards || {};
  const hero = projection?.hero || {};
  const hasPlan = Boolean(plan && projection);
  useEffect(() => {
    setActiveMobileMode("taxi");
  }, [planResponse, airport?.code, form?.direction]);
  if (!hasPlan && !loading) return null;
  if (!hasPlan && loading) {
    return (
      <section
        aria-label="Journey plan calculation"
        aria-live="polite"
        style={{
          marginTop: 14,
          minHeight: isCompact ? 112 : 96,
          padding: isCompact ? 16 : "18px 20px",
          border: "1px solid #bfdbfe",
          borderRadius: 16,
          background: "linear-gradient(135deg, #eff6ff 0%, #ffffff 100%)",
          boxShadow: "0 12px 28px rgba(15,35,70,.08)",
          display: "flex",
          alignItems: "center",
          gap: 14,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 38,
            height: 38,
            flex: "0 0 auto",
            display: "grid",
            placeItems: "center",
            borderRadius: 99,
            background: "#dbeafe",
            color: "#075bea",
            fontSize: 19,
          }}
        >
          ◔
        </span>
        <div>
          <strong style={{ display: "block", color: "#0b2b64", fontSize: isCompact ? 15 : 17 }}>
            Building your live {airport.code} journey plan…
          </strong>
          <span style={{ display: "block", marginTop: 5, color: "#536783", fontSize: 12, lineHeight: 1.4 }}>
            Checking your flight, terminal target, road traffic, rail services, fares and parking.
          </span>
        </div>
      </section>
    );
  }
  if (form?.direction === "return") return <ReturnJourneyPreview airport={airport} form={form} planResponse={planResponse} onViewDetails={onViewDetails} />;
  const recommendedMode = projection?.recommended_mode || projection?.overall?.recommended_mode || plan?.recommendation?.recommended_mode || null;
  const modes = [
    { key: "taxi", icon: "🚕", name: "Taxi & Minibus", durationDetail: "Door to terminal", routeFallback: "Direct, with no changes" },
    { key: "train", icon: "🚆", name: "Rail", durationDetail: "Home to terminal", routeFallback: "Station connections and airport transfer" },
    { key: "self_drive", icon: "🚙", name: "Self-drive", durationDetail: "Drive to terminal", routeFallback: "Traffic, parking and terminal transfer" },
  ];
  const journeyStatus = projection?.status_summary || {};
  const statusItems = [
    ["✈", "Flight", journeyStatus.flight || airport.status.flights], ["🚘", "Roads", journeyStatus.road || airport.status.road],
    ["🚆", "Rail", journeyStatus.rail || airport.status.rail], ["☁", "Weather", journeyStatus.weather || airport.status.weather],
    ["♙", "Queues", journeyStatus.queues || airport.status.queues], ["▣", "Terminal", journeyStatus.terminal || airport.status.terminals],
  ];
  const projectedSteps = Array.isArray(hero?.time_steps) ? hero.time_steps : [];
  const recommendedTimeline = projection?.timelines?.[recommendedMode] || null;
  const recommendedModeSteps = Array.isArray(recommendedTimeline?.steps) ? recommendedTimeline.steps : [];
  const terminalSteps = Array.isArray(recommendedTimeline?.terminal_common_steps)
    ? recommendedTimeline.terminal_common_steps
    : Array.isArray(hero?.terminal_common_steps)
    ? hero.terminal_common_steps
    : [];
  const stepAt = (matcher, fallback = null) => {
    const found = [...recommendedModeSteps, ...projectedSteps, ...terminalSteps].find((step) => matcher.test(String(`${step?.id || ""} ${step?.label || ""} ${step?.title || ""}`)));
    return found?.at || fallback;
  };
  const recommendedTerminalAt =
    recommendedModeSteps.find((step) => ["arrive_terminal", "gateway_to_terminal"].includes(String(step?.id || "")))?.at ||
    projectedSteps.find((step) => String(step?.id || "") === "terminal")?.at ||
    null;
  const flightAt = projection?.anchor_utc || plan?.flight?.departure || plan?.flight?.scheduled_departure || null;
  const flightDetails = projection?.flight_details || plan?.flight?.details || {};
  const flightNumber = flightDetails.flight_number || plan?.flight?.flight_number || form?.flight || "Flight";
  const showFlightStrip = Boolean(String(form?.flight || "").trim()) && !form?.manual_local && flightNumber !== "Flight";
  const destinationCode = flightDetails.arrival_airport_code || null;
  const destinationName = flightDetails.arrival_airport_name || null;
  const flightArrivalAt = flightDetails.actual_arrival || flightDetails.estimated_arrival || flightDetails.scheduled_arrival || null;
  const routeLabel = destinationCode
    ? `${airport.code} → ${destinationCode}${destinationName ? ` · ${destinationName}` : ""}`
    : `${airport.code} departure`;
  const operatorLabel = [flightDetails.airline_name || flightDetails.airline_code, flightNumber].filter(Boolean).join(" · ");
  const terminalLabel = flightDetails.terminal
    ? (/^terminal\b/i.test(String(flightDetails.terminal)) ? String(flightDetails.terminal) : `Terminal ${flightDetails.terminal}`)
    : "Terminal not confirmed";
  const terminalNumber = String(flightDetails.terminal || form.terminal || "").replace(/\D/g, "");
  const airportLocation = airportTransferLabel(airport, terminalNumber);
  const customerLocation = form.pickup_address || form.postcode;
  const conversionLinks = getAjpConversionLinks(airport.code, flightDetails.terminal);
  const trackConversion = (action, mode, provider) => {
    const eventMode = mode === "train" ? "rail" : mode;
    trackFunnelEvent(`ajp_${eventMode}_cta_click`, {
      brand: "planner",
      tracking_source: "ajp_home",
      action,
      mode,
      provider,
      airport: airport.code,
      terminal: flightDetails.terminal || "unknown",
      recommended_mode: recommendedMode || "unknown",
      ajp_journey_id: plan?.journey_id,
      direction: "to_airport",
    });
  };
  const openExternal = (url, action, mode, provider) => {
    if (!url || typeof window === "undefined") return onViewDetails();
    trackConversion(action, mode, provider);
    if (action === "parking_booking") trackFunnelEvent("ajp_parking_cta_click", { brand: "planner", tracking_source: "ajp_home", airport: airport.code, terminal: flightDetails.terminal || "unknown", provider, mode: "self_drive", direction: "to_airport", ajp_journey_id: plan?.journey_id });
    trackFunnelEvent("ajp_external_handoff_created", { brand: "planner", tracking_source: "ajp_home", airport: airport.code, terminal: flightDetails.terminal || "unknown", provider, mode: mode === "train" ? "rail" : mode, direction: "to_airport", ajp_journey_id: plan?.journey_id });
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const openTaxiEnquiry = () => {
    trackConversion("request_booking", "taxi", "ajp_enquiry_chat");
    openAjpEnquiryChat({ vehiclePreference: "Taxi", pickup: customerLocation, dropoff: airportLocation, pickupAt: leaveAt, flightNumber, flightAt, airport, terminal: terminalNumber, direction: "to_airport", journeyId: plan?.journey_id });
  };
  const openMinibusEnquiry = () => {
    trackConversion("minibus_quote", "minibus", "ajp_enquiry_chat");
    openAjpEnquiryChat({ vehiclePreference: "Minibus", pickup: customerLocation, dropoff: airportLocation, pickupAt: leaveAt, flightNumber, flightAt, airport, terminal: terminalNumber, direction: "to_airport", journeyId: plan?.journey_id });
  };
  const openDropoffCalculator = () => {
    if (!conversionLinks.dropoffPath) return onViewDetails();
    trackConversion("dropoff_calculator", "self_drive", "ajp");
    trackFunnelEvent("ajp_dropoff_cta_click", { brand: "planner", tracking_source: "ajp_home", airport: airport.code, terminal: terminalNumber || "unknown", mode: "self_drive", direction: "to_airport", ajp_journey_id: plan?.journey_id });
    const url = buildAjpDropoffUrl({
      path: conversionLinks.dropoffPath,
      airport,
      terminal: terminalNumber,
      dropoffAt: recommendedTerminalAt,
      flightNumber,
      flightAt,
      direction: "to_airport",
      journeyId: plan?.journey_id,
    });
    window.open(url, "_blank", "noopener,noreferrer");
    trackFunnelEvent("ajp_external_handoff_created", { brand: "planner", tracking_source: "ajp_home", airport: airport.code, provider: "dropoff_calculator", mode: "self_drive", direction: "to_airport", ajp_journey_id: plan?.journey_id });
  };
  const locationLabel = [terminalLabel, flightDetails.gate ? flightDetails.gate : null].filter(Boolean).join(" · ");
  const modeNames = { taxi: "Taxi", train: "Rail", rail: "Rail", self_drive: "Self-drive" };
  const recommendedCard = cards?.[recommendedMode] || {};
  const leaveAt = stepAt(/leave/i, recommendedCard?.leave_home_at);
  const journeyMins = hasPlanNumber(recommendedCard?.duration_mins) ? `${formatJourneyDuration(recommendedCard.duration_mins)} to terminal` : "Journey time checked";
  const rawFlightStatus = String(flightDetails.status_label || "").toLowerCase();
  const flightTrackingLabel = form?.manual_local
    ? "Using manually entered time"
    : rawFlightStatus.includes("schedule")
    ? "Using published schedule"
    : flightDetails.status_label || "Schedule checked";
  const timeline = [
    { icon: "⌂", label: "Leave home", at: leaveAt, detail: `${modeNames[recommendedMode] || "Recommended route"} · leave by` },
    { icon: "🚆", label: "Reach airport", at: recommendedTerminalAt, detail: "Terminal arrival target" },
    { icon: "✈", label: "Flight", at: flightAt, detail: flightTrackingLabel },
  ];
  const railCandidate = plan?.rail?.best_candidate || plan?.modes?.rail?.best_candidate || null;
  const railJourney = railCandidate?.journeys?.[0] || railCandidate?.journey || null;
  const railCard = cards?.train || cards?.rail || {};
  const railFeasibility = railCard?.feasibility || {};
  const railNotViable = ["misses_target", "limited_service"].includes(railFeasibility.state) || railCard?.recommendation_eligible === false;
  const railDepartAt = railJourney?.depart || railJourney?.departure || railJourney?.legs?.[0]?.depart || railJourney?.legs?.[0]?.sched_dep || null;
  const railStationArrivalAt = railJourney?.arrive || railJourney?.arrival || railJourney?.legs?.at?.(-1)?.arrive || railJourney?.legs?.at?.(-1)?.sched_arr || null;
  const railTerminalArrivalAt = railFeasibility?.evidence?.terminal_arrival_at || railCard?.terminal_arrival_at || railCard?.steps?.find?.((step) => step?.id === "arrive_terminal")?.at || null;
  const railArrivesAfterFlight = railTerminalArrivalAt && flightAt && new Date(railTerminalArrivalAt).getTime() >= new Date(flightAt).getTime();
  const railWarningLabel = railArrivesAfterFlight ? "Arrives after flight" : railNotViable ? "Too late for airport check-in" : null;
  const stationNames = plan?.diagnostics?.station_names || {};
  const stationName = (value) => {
    if (!value) return null;
    const raw = typeof value === "string" ? value : value?.crs || value?.code || value?.name;
    const resolved = stationNames?.[String(raw || "").toUpperCase()] || value;
    return normalizeAjpStationLabel(resolved, null);
  };
  const railLegs = Array.isArray(railJourney?.legs) ? railJourney.legs : [];
  const rawRailStops = railLegs.flatMap((leg) => [stationName(leg?.origin_name || leg?.from_name || leg?.origin || leg?.from || leg?.from_crs), stationName(leg?.destination_name || leg?.to_name || leg?.destination || leg?.to || leg?.to_crs)]).filter(Boolean);
  const stationKey = (stop) => String(stop || "").toLowerCase().replace(/^london\s+/, "").replace(/[^a-z0-9]/g, "");
  const railStops = rawRailStops.filter((stop, index) => index === 0 || stationKey(stop) !== stationKey(rawRailStops[index - 1]));
  const selectedTerminal = String(flightDetails.terminal || form?.terminal || "").replace(/\D/g, "");
  const airportRailDestination = airport.code === "LHR"
    ? selectedTerminal === "4" ? "Heathrow Terminal 4" : selectedTerminal === "5" ? "Heathrow Terminal 5" : "Heathrow Terminals 2 & 3"
    : airport.name;
  const completeRailStops = /airport|heathrow|gatwick|luton|stansted|manchester|east midlands/i.test(String(railStops[railStops.length - 1] || ""))
    ? railStops
    : [...railStops, airportRailDestination];
  const railRouteLabel = completeRailStops.length > 1 ? completeRailStops.join(" → ") : null;
  const outwardRoadDetail = plan?.road?.route_detail || plan?.modes?.self_drive?.road?.route_detail || plan?.modes?.taxi?.road?.route_detail || plan?.road_plan?.route_detail || null;
  const outwardRoadSteps = Array.isArray(outwardRoadDetail?.steps) ? outwardRoadDetail.steps : [];
  const outwardRoadNames = [...new Set(outwardRoadSteps.flatMap((step) => String(`${step?.name || ""} ${step?.instruction || ""}`).toUpperCase().match(/\b(?:M\d{1,3}[A-Z]?|A\d{1,4}[A-Z]?)\b/g) || []))];
  const outwardRoadRouteLabel = [customerLocation || "Home", ...outwardRoadNames.slice(0, 4), airportLocation].filter((point, index, list) => point && (index === 0 || point !== list[index - 1])).join(" → ");
  const compactRoadRoute = <div title={outwardRoadRouteLabel} style={{ minHeight: 20, marginTop: 7, padding: "4px 7px", borderRadius: 6, background: "rgba(255,255,255,.48)", color: "#40536e", fontSize: 9, lineHeight: 1.25, fontWeight: 800, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>🚘 {outwardRoadRouteLabel}</div>;
  return (
    <section className="ajp-plan-results" aria-label="Journey plan preview" style={{ marginTop: 14, border: "1px solid #dce5f0", borderRadius: 18, background: "rgba(255,255,255,.97)", boxShadow: "0 18px 42px rgba(15,35,70,.12)", overflow: "hidden" }}>
      <div style={{ padding: isCompact ? 14 : "16px 20px 12px", background: "linear-gradient(135deg,#ffffff,#f4f8ff)", borderBottom: "1px solid #dce5f0" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <strong style={{ color: "#0b2b64", fontSize: isCompact ? 20 : 25 }}>{hasPlan ? `Your best ways to ${airport.name}` : "Your journey, minute by minute"}</strong>
          <span style={{ padding: "6px 10px", borderRadius: 99, background: "#e7f8ec", color: "#15803d", fontSize: 11, fontWeight: 850 }}>✓ {hasPlan ? projection?.checks_label || "Live conditions checked" : "Conditions checked when you build"}</span>
        </div>
        {hasPlan && showFlightStrip ? <AjpFlightStrip flightNumber={flightNumber} airline={flightDetails.airline_name || flightDetails.airline_code} originCode={flightDetails.departure_airport_code || airport.code} originName={flightDetails.departure_airport_name || airport.name} departureAt={flightAt} originTerminal={terminalLabel} originGate={flightDetails.gate} originZone={flightDetails.zone || flightDetails.departure_zone} destinationCode={destinationCode} destinationName={destinationName} arrivalAt={flightArrivalAt} isCompact={isCompact} /> : null}
        <div style={{ marginTop: 7, padding: isCompact ? "7px 5px" : "5px 8px", display: "grid", gridTemplateColumns: `repeat(${timeline.length},1fr)`, gap: isCompact ? 4 : 18, border: "1px solid #c8dcf5", borderRadius: 10, background: "linear-gradient(90deg,#e8f2ff,#f3f8ff)" }}>
          {timeline.map(({ icon, label, at, detail }, index) => <div key={label} style={{ position: "relative", minWidth: 0, display: "grid", gridTemplateColumns: isCompact ? "1fr" : "32px minmax(0,1fr)", gap: isCompact ? 3 : 7, alignItems: "center", justifyItems: isCompact ? "center" : "stretch", padding: "3px 4px", textAlign: isCompact ? "center" : "left" }}>
            {!isCompact && index < timeline.length - 1 ? <span aria-hidden="true" style={{ position: "absolute", zIndex: 0, left: 38, right: -14, top: "50%", borderTop: "1px dashed #91add3" }} /> : null}
            <span style={{ position: "relative", zIndex: 1, width: 28, height: 28, display: "grid", placeItems: "center", borderRadius: 99, background: "#d8e9ff", fontSize: 13 }}>{icon}</span><span style={{ position: "relative", zIndex: 1, minWidth: 0, maxWidth: "100%" }}><small style={{ display: "block", color: "#64748b", fontSize: 9 }}>{label}</small><b style={{ display: "block", color: "#0f2f63", fontSize: 15, lineHeight: 1 }}>{hasPlan ? formatPlanTime(at) : "—:—"}</b>{!isCompact ? <small title={detail} style={{ display: "block", marginTop: 1, color: "#64748b", fontSize: 8, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label === "Flight" ? `${flightNumber}${destinationCode ? ` to ${destinationCode}` : ""} · ${flightTrackingLabel}` : detail}</small> : null}</span>
          </div>)}
        </div>
      </div>
      <div style={{ padding: isCompact ? 14 : 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "end", gap: 12, marginBottom: 11 }}><div><strong style={{ color: "#0b2b64", fontSize: 16 }}>Compare three ways to get there</strong><div style={{ color: "#64748b", fontSize: 12, marginTop: 3 }}>{isCompact ? "Tap an option to see its full journey, time and cost." : "Times and fares are calculated from your postcode and flight."}</div></div></div>
        {isCompact ? <div role="tablist" aria-label="Choose a way to the airport" style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 7, marginBottom: 12 }}>
          {modes.map((mode) => {
            const card = cards?.[mode.key] || (mode.key === "train" ? cards?.rail : null) || {};
            const selected = activeMobileMode === mode.key;
            const recommended = recommendedMode === mode.key || card.recommended === true;
            const cost = hasPlanNumber(card.cost_gbp) ? `£${Number(card.cost_gbp).toFixed(0)}` : "Check";
            return <button className="ajp-mode-tab" key={mode.key} type="button" role="tab" aria-selected={selected} onClick={() => setActiveMobileMode(mode.key)} style={{ position: "relative", minWidth: 0, minHeight: 80, padding: "8px 7px", borderRadius: 12, border: selected ? "2px solid #1264ed" : "1px solid #c8dcf5", background: selected ? "#dbeafe" : "#eef6ff", color: "#0b2b64", boxShadow: selected ? "0 8px 18px rgba(18,100,237,.14)" : "none", cursor: "pointer", textAlign: "left", overflow: "hidden" }}>
              <span style={{ display: "flex", alignItems: "center", minHeight: 20 }}><span aria-hidden="true" style={{ fontSize: 17 }}>{mode.icon}</span>{recommended ? <small style={{ position: "absolute", top: 8, right: 5, width: 61, padding: "3px 2px", borderRadius: 99, background: "#173f7a", color: "#fff", fontSize: 6.5, lineHeight: 1, fontWeight: 950, letterSpacing: ".015em", textAlign: "center", whiteSpace: "nowrap" }}>RECOMMENDED</small> : null}</span>
              <strong style={{ display: "block", marginTop: 5, fontSize: 11, lineHeight: 1.15 }}>{mode.name}</strong>
              <span style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 4, marginTop: 3 }}><small style={{ color: "#526783", fontSize: 10, fontWeight: 800 }}>From {cost}</small><small style={{ flex: "0 0 auto", color: selected ? "#1254c4" : "#526783", fontSize: selected ? 7.5 : 15, lineHeight: 1, fontWeight: 900 }}>{selected ? "Viewing" : <span aria-hidden="true">›</span>}</small></span>
            </button>;
          })}
        </div> : null}
        <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "repeat(3,minmax(0,1fr))", gap: 12 }}>
          {modes.map((mode) => {
            const card = cards?.[mode.key] || (mode.key === "train" ? cards?.rail : null) || {};
            const recommended = recommendedMode === mode.key || card.recommended === true;
            const style = AJP_MODE_STYLES[mode.key];
            const cost = hasPlanNumber(card.cost_gbp) ? `£${Number(card.cost_gbp).toFixed(2)}` : "Check fare";
            const duration = hasPlanNumber(card.duration_mins) ? formatJourneyDuration(card.duration_mins) : "Check journey";
            const route = card.route_label || card.via || card.insight || card.summary || mode.routeFallback;
            const fareDetail = card.cost_label || (mode.key === "taxi" ? "Estimated total fare" : mode.key === "self_drive" ? "Estimated parking and driving cost" : "Estimated fare");
            const railParty = card?.cost_breakdown?.party || planResponse?.ajp_v2?.party || plan?.ajp_v2?.party || { adults: 2, children: 2 };
            const railFareHeading = "Total fare";
            const railChanges = hasPlanNumber(card?.cost_breakdown?.changes) ? Number(card.cost_breakdown.changes) : Math.max(0, railLegs.length - 1);
            const railJourneyDetail = hasPlanNumber(card.duration_mins)
              ? `${formatJourneyDuration(card.duration_mins)} · ${railChanges} ${railChanges === 1 ? "change" : "changes"}`
              : railChanges > 0 ? `${railChanges} ${railChanges === 1 ? "change" : "changes"}` : null;
            const selfDriveBreakdown = card?.cost_breakdown || {};
            const selfDriveCostDetail = [
              hasPlanNumber(selfDriveBreakdown.parking_gbp) ? `£${Number(selfDriveBreakdown.parking_gbp).toFixed(2)} parking` : null,
              hasPlanNumber(selfDriveBreakdown.return_fuel_gbp) ? `£${Number(selfDriveBreakdown.return_fuel_gbp).toFixed(2)} return fuel` : null,
            ].filter(Boolean).join(" + ");
            const isLateRail = mode.key === "train" && railNotViable;
            const cardBackground = mode.key === "taxi" ? "linear-gradient(145deg,#fff0b8,#ffe59a)" : mode.key === "train" ? (isLateRail ? "linear-gradient(145deg,#f1f3f5,#e5e7eb)" : "linear-gradient(145deg,#e4f6e9,#d5f0df)") : "linear-gradient(145deg,#e7f1ff,#d9e9ff)";
            const primary = mode.key === "train"
              ? { label: "Check fares & tickets ↗", onClick: () => openExternal(conversionLinks.railUrl, "rail_booking", "train", "national_rail") }
              : mode.key === "taxi"
              ? { label: "Request a booking", onClick: openTaxiEnquiry }
              : { label: conversionLinks.parkingUrl ? "Book airport parking ↗" : "View drive plan", onClick: () => conversionLinks.parkingUrl ? openExternal(conversionLinks.parkingUrl, "parking_booking", "self_drive", "official_airport") : onViewDetails() };
            const secondary = mode.key === "train"
              ? { label: "View rail plan", onClick: onViewDetails }
              : mode.key === "taxi"
              ? { label: "Call us", onClick: () => { trackConversion("call", "taxi", "cheapcabgrab"); window.location.href = conversionLinks.taxiCallHref; } }
              : { label: conversionLinks.dropoffPath ? "Drop-off calculator" : "View drive plan", onClick: conversionLinks.dropoffPath ? openDropoffCalculator : onViewDetails };
            const disclaimer = mode.key === "train" ? "Live fares may change · Opens National Rail" : mode.key === "taxi" ? "No obligation · Journey details can be confirmed in chat" : conversionLinks.dropoffPath ? "Official airport parking · Drop-off calculator opens in a new tab" : "Opens the airport’s official parking site";
            if (isCompact && activeMobileMode !== mode.key) return null;
            return <article key={mode.name} role={isCompact ? "tabpanel" : undefined} style={{ display: "flex", flexDirection: "column", background: isCompact && !isLateRail ? "linear-gradient(145deg,#f2f7ff,#e7f1ff)" : cardBackground, border: `${recommended ? 2 : 1}px solid ${recommended ? (isCompact ? "#173f7a" : "#d99000") : isLateRail ? "#9ca3af" : style.border}`, borderRadius: 14, padding: isCompact ? 9 : 12, boxShadow: recommended ? "0 9px 22px rgba(23,63,122,.16)" : "0 6px 16px rgba(15,47,99,.07)", overflow: "hidden" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 7, alignItems: "center", padding: isCompact ? "7px 8px" : 0, margin: isCompact ? "-1px -1px 0" : 0, border: isCompact ? `1px solid ${isLateRail ? "#9ca3af" : style.border}` : 0, borderRadius: isCompact ? 10 : 0, background: isCompact ? (mode.key === "taxi" ? "#fff3c9" : mode.key === "train" ? (isLateRail ? "#e5e7eb" : "#e8f6ec") : "#e5f0ff") : "transparent" }}><strong style={{ minWidth: 0, color: "#0b2b64", fontSize: isCompact ? 15 : 17, lineHeight: 1.15 }}>{mode.icon} {mode.name}</strong><span style={{ flex: "0 0 auto", color: recommended ? "#fff" : isLateRail ? "#7f1d1d" : statusTone(card.status || "checking").fg, background: recommended ? "#173f7a" : isLateRail ? "#fee2e2" : statusTone(card.status || "checking").bg, borderRadius: 99, padding: isCompact ? "3px 7px" : "4px 8px", fontSize: isCompact ? 8 : 9, lineHeight: 1.1, fontWeight: 900, whiteSpace: "nowrap" }}>{recommended ? (mode.key === "taxi" ? "BEST FOR GROUPS" : "RECOMMENDED") : isLateRail ? "NOT VIABLE" : hasPlan ? statusLabel(card.status) : "Checking…"}</span></div>
              {mode.key === "taxi" ? <>
                <div style={{ marginTop: isCompact ? 7 : 9, padding: isCompact ? 7 : "9px 10px", display: "grid", gridTemplateColumns: isCompact ? "68px minmax(0,1fr)" : "82px minmax(0,1fr)", gap: isCompact ? 7 : 9, alignItems: "center", border: "1px solid #f2cf76", borderRadius: 10, background: "rgba(255,255,255,.76)" }}>
                  <span aria-hidden="true" style={{ alignSelf: "stretch", minHeight: isCompact ? 60 : 72, borderRadius: 8, background: "url('/ajp-minibus-card.webp') 69% center / cover no-repeat", boxShadow: "inset 0 0 0 1px rgba(124,74,3,.12)" }} /><span><b style={{ display: "block", color: "#0f2f63", fontSize: isCompact ? 12 : 14, lineHeight: 1.25 }}>Minibus for 5–16 passengers</b><small style={{ display: "block", margin: "2px 0 5px", color: "#52657f", fontSize: isCompact ? 9.5 : undefined }}>Get a group quote for this journey.</small><button className="ajp-minibus-cta" type="button" onClick={openMinibusEnquiry} style={{ width: "100%", minHeight: isCompact ? 30 : 34, border: 0, borderRadius: 8, background: "#fbbf24", color: "#14213d", fontSize: isCompact ? 11 : undefined, fontWeight: 900, cursor: "pointer" }}>Get minibus quote →</button></span>
                </div>
                <div style={{ marginTop: 8, padding: "8px 9px", display: "grid", gridTemplateColumns: isCompact ? "58px minmax(0,1fr)" : "62px minmax(0,1fr) auto", gap: 8, alignItems: "center", border: "1px solid #dfb442", borderRadius: 10, background: "rgba(255,255,255,.72)" }}><span aria-hidden="true" style={{ width: isCompact ? 58 : 62, height: 42, borderRadius: 7, background: "url('/ajp-taxi-card.webp') 72% center / cover no-repeat", boxShadow: "inset 0 0 0 1px rgba(7,31,82,.1)" }} /><span><b style={{ display: "block", color: "#071f52", fontSize: isCompact ? 13 : 15, fontWeight: 950 }}>Private taxi {hasPlanNumber(card.cost_gbp) ? `from ${cost}` : "quote"}</b><small style={{ color: "#3f526c", fontSize: isCompact ? 10 : undefined, fontWeight: 700 }}>{duration} · Door to terminal</small></span><button className="ajp-taxi-cta" type="button" onClick={openTaxiEnquiry} style={{ gridColumn: isCompact ? "1 / -1" : undefined, width: isCompact ? "100%" : "auto", minHeight: 34, padding: isCompact ? "0 12px" : "0 18px", border: `1px solid ${style.accent}`, borderRadius: 8, background: "#fff", color: style.accent, fontSize: isCompact ? 12 : undefined, fontWeight: 900, cursor: "pointer" }}>Book taxi →</button></div>
                {compactRoadRoute}
                <small style={{ marginTop: "auto", paddingTop: 8, color: "#64748b" }}>Direct to the terminal · No obligation</small>
              </> : <>
                <div aria-hidden="true" style={{ height: 70, marginTop: 9, borderRadius: 10, border: `1px solid ${style.border}`, backgroundImage: `linear-gradient(90deg,rgba(255,255,255,.08),rgba(255,255,255,0)),url('${mode.key === "train" ? "/ajp-rail-card.webp" : "/ajp-parking-card.webp"}')`, backgroundSize: "cover", backgroundPosition: mode.key === "train" ? "center 54%" : "center 58%", boxShadow: "inset 0 -20px 26px rgba(7,31,82,.08)" }} />
                <div style={{ margin: isCompact ? "9px 0 7px" : "12px 0 9px" }}><small style={{ display: "block", color: "#52657f", fontSize: isCompact ? 10 : undefined, fontWeight: 800 }}>{mode.key === "train" ? railFareHeading : "Journey time"}</small><b style={{ display: "block", color: "#0b2b64", fontSize: isCompact ? 20 : 22 }}>{mode.key === "train" ? cost : duration}</b><strong style={{ display: "block", marginTop: 2, color: "#3f526c", fontSize: isCompact ? 10 : 11 }}>{mode.key === "train" ? `${railParty.adults ?? 2} adults + ${railParty.children ?? 2} children · ${fareDetail}` : selfDriveCostDetail || (hasPlanNumber(card.cost_gbp) ? `${cost} estimated total` : fareDetail)}</strong>{mode.key === "train" && railJourneyDetail ? <strong style={{ display: "block", marginTop: 4, color: isLateRail ? "#4b5563" : "#174c2b", fontSize: isCompact ? 10 : 11 }}>{railJourneyDetail}</strong> : null}{mode.key === "train" && railWarningLabel ? <div role="status" style={{ marginTop: 7, padding: "7px 8px", border: "1px solid #f3b4b4", borderRadius: 8, background: "#fff1f2", color: "#991b1b", fontSize: isCompact ? 10 : 11, lineHeight: 1.35, fontWeight: 850 }}><b style={{ display: "block" }}>{railWarningLabel}</b><span style={{ display: "block", marginTop: 2, fontWeight: 700 }}>{railDepartAt ? `Train leaves ${formatPlanTime(railDepartAt)}` : "Earliest train shown"}{railStationArrivalAt ? ` · station ${formatPlanTime(railStationArrivalAt)}` : ""}{railTerminalArrivalAt ? ` · terminal ${formatPlanTime(railTerminalArrivalAt)}` : ""}{flightAt ? ` · flight ${formatPlanTime(flightAt)}` : ""}</span></div> : null}{mode.key === "train" ? <div style={{ marginTop: 7, paddingTop: 6, borderTop: `1px solid ${isLateRail ? "rgba(107,114,128,.25)" : "rgba(28,125,65,.2)"}`, color: isLateRail ? "#4b5563" : "#174c2b", fontSize: isCompact ? 10 : 11, lineHeight: 1.4, fontWeight: 800 }}>🚆 {railRouteLabel || route}</div> : compactRoadRoute}</div>
                <div style={{ marginTop: "auto", display: "grid", gridTemplateColumns: secondary ? "minmax(0,1.35fr) minmax(0,.8fr)" : "1fr", gap: 7 }}><button className="ajp-mode-primary-cta" type="button" onClick={hasPlan ? primary.onClick : onOpenPlan} disabled={loading} style={{ minHeight: isCompact ? 34 : 38, padding: isCompact ? "5px 7px" : undefined, borderRadius: 9, border: `1px solid ${style.accent}`, background: style.accent, color: "#fff", fontSize: isCompact ? 12 : undefined, lineHeight: 1.2, fontWeight: 850, cursor: loading ? "wait" : "pointer" }}>{loading ? "Building…" : hasPlan ? primary.label : "Add journey details"}</button><button className="ajp-mode-secondary-cta" type="button" onClick={hasPlan ? secondary.onClick : onOpenPlan} disabled={loading} style={{ minHeight: isCompact ? 34 : 38, padding: isCompact ? "5px 7px" : undefined, borderRadius: 9, border: `1px solid ${style.accent}`, background: "rgba(255,255,255,.78)", color: style.accent, fontSize: isCompact ? 11 : undefined, lineHeight: 1.2, fontWeight: 850, cursor: loading ? "wait" : "pointer" }}>{hasPlan ? secondary.label : "View plan"}</button></div>
                <small style={{ marginTop: 6, color: "#64748b", fontSize: isCompact ? 10 : undefined, lineHeight: 1.3 }}>{mode.key === "train" ? "Fare and final mile clearly shown" : "Parking, fuel and terminal transfer included"}</small>
              </>}
            </article>;
          })}
        </div>
        <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 12, background: "linear-gradient(90deg,#e8f4ff,#f4f9ff)", border: "1px solid #cfe2f6", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}><div style={{ display: "flex", gap: 10, alignItems: "center" }}><span style={{ width: 34, height: 34, display: "grid", placeItems: "center", borderRadius: 99, background: "#1264ed", color: "#fff" }}>●●●</span><span><strong style={{ display: "block", color: "#0b2b64", fontSize: 13 }}>Not sure which option is best?</strong><small style={{ color: "#526783" }}>Get friendly advice based on your flight, group size and preferences.</small></span></div><JourneyAlertConversion airportCode={airport.code} postcode={form.postcode} flightNumber={form.flight} flightAnchorUtc={form.flight_anchor_utc || form.manual_local || flightAt} journeyId={plan?.journey_id} direction={form.direction === "return" ? "from_airport" : "to_airport"} placement="homepage-plan-result" label="Talk through my journey →" /></div>
      </div>
    </section>
  );
}

function AirportPulseStrip({ airportList, selectedKey, onSelect }) {
  const isCompact = useCompactMobile(820);
  return (
    <section style={{ marginTop: 16 }} aria-label="Live status across six UK airports">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "end", gap: 12, marginBottom: 9 }}>
        <div>
          <strong style={{ color: "#0b2b64", fontSize: isCompact ? 16 : 19 }}>Live across 6 UK airports</strong>
          <div style={{ marginTop: 2, color: "#64748b", fontSize: 12 }}>Select an airport to view its full live intelligence.</div>
        </div>
        <span style={{ color: "#15803d", fontSize: 11, fontWeight: 850, whiteSpace: "nowrap" }}>● Latest operational snapshot</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: isCompact ? "repeat(2,minmax(0,1fr))" : "repeat(6,minmax(0,1fr))", gap: isCompact ? 8 : 9 }}>
        {airportList.map((airport) => {
          const selected = airport.key === selectedKey;
          const tone = statusTone(airport.status.overall);
          const fallbackSignals = [
            { key: "flights", name: "Flights", detail: airport.statusHeadlines?.flights || "Flight status", label: airport.status.flights },
            { key: "terminals", name: "Terminal", detail: airport.statusHeadlines?.terminals || "Terminal status", label: airport.status.terminals },
            { key: "queues", name: "Queues", detail: airport.statusHeadlines?.queues || "Queue status", label: airport.status.queues },
            { key: "road", name: "Road", detail: airport.statusHeadlines?.road || "Road corridors", label: airport.status.road },
            { key: "rail", name: "Rail", detail: airport.statusHeadlines?.rail || "Rail services", label: airport.status.rail },
            { key: "weather", name: "Weather", detail: airport.preview?.weather || "Weather status", label: airport.status.weather },
          ];
          const signals = airport.miniStatus?.length ? airport.miniStatus : fallbackSignals;
          const worstSignal = signals
            .slice()
            .sort((a, b) => statusRank(b?.label) - statusRank(a?.label))[0];
          const worstStatus = worstSignal?.label || "Checking";
          const exceptionText = statusRank(worstStatus) > 2
            ? `${worstSignal.name}: ${worstSignal.detail || statusLabel(worstStatus)}`
            : statusLabel(airport.status.overall) === "Checking…"
            ? "Live checks updating"
            : "No major issues detected";
          return (
            <button
              key={airport.key}
              type="button"
              onClick={() => onSelect(airport.key)}
              aria-pressed={selected}
              style={{
                minWidth: 0,
                minHeight: isCompact ? 112 : 120,
                padding: isCompact ? "11px 10px" : "13px 14px",
                borderRadius: 13,
                border: selected ? "2px solid #2563eb" : "1px solid #dce5f0",
                background: selected ? "#eef5ff" : "rgba(255,255,255,.96)",
                boxShadow: selected ? "0 9px 22px rgba(37,99,235,.14)" : "0 5px 14px rgba(15,35,70,.05)",
                textAlign: "left",
                cursor: "pointer",
              }}
            >
              <span style={{ display: "flex", justifyContent: "space-between", gap: 5, color: "#0b2b64", fontWeight: 900, fontSize: isCompact ? 11 : 13 }}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>✈ {airport.name}</span>
                <small style={{ color: "#64748b", fontSize: 9 }}>{airport.code}</small>
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 8, color: tone.fg, fontWeight: 900, fontSize: isCompact ? 10 : 12 }}>
                <span style={{ width: 7, height: 7, flex: "0 0 auto", borderRadius: 99, background: tone.fg }} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{statusLabel(airport.status.overall)}</span>
              </span>
              <span title={exceptionText} style={{ display: "block", marginTop: 10, paddingTop: 9, borderTop: "1px solid #e3ebf7", color: "#526783", fontSize: isCompact ? 10 : 11, lineHeight: 1.35, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {exceptionText}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function PlannerEntryCard({ airport, form, onChange, onSubmit }) {
  const isCompact = useCompactMobile();
  const compactInputStyle = isCompact
    ? { ...inputStyle, minHeight: 40, padding: "8px 10px", fontSize: 13, borderRadius: 10 }
    : inputStyle;
  const compactButtonStyle = isCompact
    ? { ...buttonStyle, minHeight: 42, fontSize: 13, borderRadius: 10 }
    : buttonStyle;

  return (
    <section
      style={{
        background: "#ffffff",
        border: "1px solid #dbe7ff",
        borderRadius: 14,
        padding: 12,
        boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 10px",
              borderRadius: 999,
              background: `${airport.accent}14`,
              color: airport.accent,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.02em",
              textTransform: "uppercase",
            }}
          >
            {airport.name} Live
          </div>
          <h3 style={{ margin: "7px 0 3px", fontSize: 19, lineHeight: 1.1, color: "#0f2f6d" }}>
            Open the {airport.code} planner
          </h3>
          <p style={{ margin: 0, fontSize: 12, lineHeight: 1.4, color: "#38507b", maxWidth: 560 }}>
            {airport.summary}
          </p>
        </div>
        <div
          style={{
            minWidth: 78,
            padding: "7px 9px",
            borderRadius: 12,
            background: "#f8fbff",
            border: "1px solid #dbe7ff",
            textAlign: "center",
            display: isCompact ? "none" : "block",
          }}
        >
          <div style={{ fontSize: 11, color: "#5d749c", fontWeight: 700 }}>Overall</div>
          <div style={{ marginTop: 3, fontSize: 15, color: "#0f2f6d", fontWeight: 800 }}>{statusLabel(airport.status.overall)}</div>
        </div>
      </div>

      <div
        style={{
          marginTop: 10,
          display: "grid",
          gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1.2fr) minmax(0, 1fr) minmax(180px, 260px)",
          gap: 10,
        }}
      >
        <label style={{ display: "grid", gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#38507b" }}>From postcode</span>
          <input
            value={form.postcode}
            onChange={(e) => onChange(airport.key, "postcode", e.target.value)}
            placeholder="e.g. LE1 1GB"
            style={compactInputStyle}
          />
        </label>
        <label style={{ display: "grid", gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#38507b" }}>Flight number (optional)</span>
          <input
            value={form.flight}
            onChange={(e) => onChange(airport.key, "flight", e.target.value.toUpperCase())}
            placeholder={airport.key === "heathrow" ? "e.g. BA123" : "e.g. FR669"}
            style={compactInputStyle}
          />
        </label>
        <div style={{ display: "grid", gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#38507b" }}>Open planner</span>
          <button
            type="button"
            onClick={() => onSubmit(airport.key)}
            style={{
              ...compactButtonStyle,
              background: airport.accent,
              boxShadow: `0 12px 24px ${airport.accent}33`,
            }}
          >
            Open {airport.code} planner
          </button>
        </div>
      </div>

      <div
        style={{
          marginTop: 10,
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          fontSize: 11,
          color: "#567097",
          fontWeight: 600,
        }}
      >
        <span>Live road and rail checks</span>
        <span>Flight-aware timing</span>
        <span>No sign-up required to start</span>
      </div>
    </section>
  );
}

function AirportStatusCard({ airport, onOpen }) {
  const isCompact = useCompactMobile();
  const statusRows = [
    { key: "road", label: "Roads", value: airport.status.road },
    { key: "rail", label: "Rail", value: airport.status.rail },
    { key: "flights", label: "Flights", value: airport.status.flights },
    { key: "terminals", label: "Terminals", value: airport.status.terminals },
    { key: "queues", label: "Queues", value: airport.status.queues },
    { key: "weather", label: "Weather", value: airport.status.weather },
  ];
  const overallText = readableStatusText(airport, "overall", airport.statusReasons?.overall || airport.liveReason || "");

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={() => onOpen?.(airport.key)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") onOpen?.(airport.key);
      }}
      style={{
        background: "rgba(255,255,255,0.94)",
        border: "1px solid rgba(191, 210, 239, 0.95)",
        borderRadius: 22,
        padding: 0,
        overflow: "hidden",
        boxShadow: "0 20px 42px rgba(15, 23, 42, 0.12)",
        cursor: "pointer",
        backdropFilter: "blur(16px)",
      }}
      title={overallText || "Open live status preview"}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
          alignItems: "center",
          padding: isCompact ? "11px 12px" : "12px 16px",
          background:
            airport.code === "LHR"
              ? "linear-gradient(90deg, #061b46 0%, #0d2f73 100%)"
              : "linear-gradient(90deg, #0b4bd1 0%, #1d63f2 100%)",
          color: "#eff6ff",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span style={{ fontSize: 18 }}>✈️</span>
          <div style={{ fontSize: isCompact ? 15 : 16, fontWeight: 900, lineHeight: 1.15 }}>{airport.name} ({airport.code})</div>
        </div>
        <div style={{ flex: "0 0 auto", padding: isCompact ? "4px 8px" : "4px 10px", borderRadius: 999, background: "rgba(34,197,94,0.16)", color: "#c7f9d4", fontSize: 10, fontWeight: 900 }}>
          ● LIVE
        </div>
      </div>
      <div style={{ display: "grid", padding: isCompact ? "8px 12px 12px" : "10px 16px 14px" }}>
        {statusRows.map((row) => {
          const tone = statusTone(row.value);
          const readableText = readableStatusHeadline(airport, row.key) || row.value;
          return (
            <div
              key={`${airport.key}-${row.label}`}
              title={readableText}
              style={{
                display: "grid",
                gridTemplateColumns: isCompact ? "minmax(0, 1fr) auto" : "78px minmax(0, 1fr) auto",
                gap: isCompact ? "5px 8px" : 10,
                alignItems: "center",
                padding: "8px 0",
                borderTop: row.key === "road" ? "none" : "1px solid rgba(203, 213, 225, 0.6)",
              }}
            >
              <span style={{ color: "#243b63", fontWeight: 800, fontSize: 13 }}>
                <span style={{ marginRight: 8 }}>{sourceIcons[row.key]}</span>
                {row.label}
              </span>
              <span style={{ color: "#1d355d", fontWeight: 700, fontSize: 13, overflow: "hidden", textOverflow: isCompact ? "clip" : "ellipsis", whiteSpace: isCompact ? "normal" : "nowrap", gridColumn: isCompact ? "1 / -1" : "auto", gridRow: isCompact ? 2 : "auto", lineHeight: 1.35 }}>
                {readableText}
              </span>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 82,
                  padding: "4px 9px",
                  borderRadius: 999,
                  background: tone.bg,
                  color: tone.fg,
                  fontSize: 11,
                  fontWeight: 800,
                  gridColumn: isCompact ? 2 : "auto",
                  gridRow: isCompact ? 1 : "auto",
                }}
              >
                {statusLabel(row.value)}
              </span>
            </div>
          );
        })}
      </div>
      <div
        style={{
          marginTop: 8,
          padding: isCompact ? "10px 12px 0" : "10px 16px 0",
          paddingTop: 10,
          borderTop: "1px solid #e5eefc",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span style={{ fontSize: 13, color: "#243b63", fontWeight: 900 }}>{sourceIcons.overall} Overall</span>
        <span
          style={{
            minWidth: isCompact ? 100 : 130,
            textAlign: "center",
            padding: "7px 12px",
            borderRadius: 999,
            background: statusTone(airport.status.overall).bg,
            color: statusTone(airport.status.overall).fg,
            fontSize: 13,
            fontWeight: 900,
          }}
        >
          {statusLabel(airport.status.overall)}
        </span>
      </div>
      <div style={{ marginTop: 10, padding: isCompact ? "0 12px 14px" : "0 16px 14px", fontSize: 12, color: "#587097", fontWeight: 700, lineHeight: 1.4 }}>
        {overallText || "Tap for live status details"}
      </div>
    </article>
  );
}

const asPreviewList = (value, fallback = []) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value) return [value];
  return Array.isArray(fallback) ? fallback.filter(Boolean) : [];
};

const stripStatusText = (text) =>
  String(text || "")
    .replace(/\s+/g, " ")
    .replace(/\s*•\s*/g, " • ")
    .trim();

const parseSignalText = (text, fallbackStatus = "Checking") => {
  const clean = stripStatusText(text);
  const [titlePart, ...detailParts] = clean.split(" • ");
  const maybeStatus = detailParts[detailParts.length - 1] || "";
  const statusWithDetail = maybeStatus.match(/^([a-z _-]+)\s*\(([^)]+)\)$/i);
  const parsedStatus = statusWithDetail?.[1]?.trim();
  const parsedDetail = statusWithDetail?.[2]?.trim();
  const statusCandidate = parsedStatus || maybeStatus;
  const status = /good|clear|on time|moderate|busy|watch|severe|red|amber|delay|slow|checking/i.test(statusCandidate)
    ? statusCandidate.replace(/ service$/i, "").trim()
    : fallbackStatus;
  const detail = parsedDetail
    || (/ service$/i.test(statusCandidate) ? statusCandidate : "")
    || (detailParts.length > 1
      ? detailParts.slice(0, -1).join(" • ")
      : detailParts[0] && detailParts[0] !== statusCandidate
        ? detailParts[0]
        : "");
  return {
    title: titlePart || clean || "Status checking",
    detail,
    status,
  };
};

const terminalSortValue = (terminal) => {
  const match = String(terminal || "").match(/\d+/);
  return match ? Number(match[0]) : 99;
};

const terminalQueueCards = (airport) => {
  const queues = Array.isArray(airport.preview.terminal_queues)
    ? airport.preview.terminal_queues.filter((item) => item?.terminal)
    : [];
  if (queues.length) {
    return [...queues].sort((a, b) => terminalSortValue(a.terminal) - terminalSortValue(b.terminal));
  }

  const terminalParts = String(airport.preview.terminal || "")
    .split("•")
    .map((item) => item.trim())
    .filter(Boolean);
  if (terminalParts.length) return terminalParts;

  return airport.code === "LHR"
    ? ["T2 Checking", "T3 Checking", "T4 Checking", "T5 Checking"]
    : [airport.preview.terminal || "Terminal Checking"];
};

const bestRoadSignals = (airport) =>
  asPreviewList(airport.preview.road, [airport.statusHeadlines?.road || airport.statusReasons?.road])
    .slice(0, airport.code === "LHR" ? 4 : 3)
    .map((item) => parseSignalText(item, airport.status.road));

const bestRailSignals = (airport) => {
  const fallback = airport.code === "LHR"
    ? ["Heathrow T2/T3 • Checking", "Heathrow T4 • Checking", "Heathrow T5 • Checking", "Underground • Checking"]
    : ["East Midlands Parkway • Checking", "Derby • Checking", "Nottingham • Checking"];
  return asPreviewList(airport.preview.rail, fallback)
    .slice(0, airport.code === "LHR" ? 4 : 3)
    .map((item) => parseSignalText(item, airport.status.rail));
};

const flightSignalCards = (airport) => {
  const flightStatus = readableFlightText(airport, airport.statusReasons?.flights || airport.statusHeadlines?.flights || airport.preview.flights?.[0]);
  const cards = [
    {
      title: "Current flight pressure",
      detail: flightStatus,
      status: airport.status.flights,
    },
    {
      title: "Airport flow",
      detail: `${airport.preview.arrivals || "0"} arrivals • ${airport.preview.departures || "0"} departures`,
      status: airport.status.flights,
    },
  ];
  const sample = asPreviewList(airport.preview.flights).find(Boolean);
  if (sample) {
    cards.push({
      title: "Latest visible service",
      detail: stripStatusText(sample),
      status: airport.status.flights,
    });
  }
  return cards;
};

const queueSignals = (airport) =>
  asPreviewList(airport.preview.queues, [airport.statusHeadlines?.queues || airport.statusReasons?.queues])
    .slice(0, 4)
    .map((item) => parseSignalText(item, airport.status.queues));

const terminalPressureCards = (airport) => {
  const structured = Array.isArray(airport.preview.terminal_pressure)
    ? airport.preview.terminal_pressure.filter((item) => item && typeof item === "object")
    : [];
  if (structured.length) return structured;
  const terminalParts = String(airport.preview.terminal || "")
    .split("•")
    .map((item) => item.trim())
    .filter(Boolean);
  if (terminalParts.length) return terminalParts;
  return airport.code === "LHR"
    ? ["T2 Checking", "T3 Checking", "T4 Checking", "T5 Checking"]
    : [airport.preview.terminal || "Main Terminal Checking"];
};

function PreviewPanel({ airport, onOpenPlanner }) {
  const isCompact = useCompactMobile();
  const detail = airport.detailedStatus || {};
  const formatClock = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  };
  const movementLine = (row, verb) => {
    if (!row) return null;
    const rawStatus = String(row.status_text || "").trim();
    const at = row.actual_utc || row.estimated_utc || row.scheduled_utc;
    const clock = row.effective_time_local || formatClock(at);
    if (/cancel/i.test(rawStatus)) {
      return `${row.flight_number || "Flight"} cancelled${clock ? ` · scheduled ${clock}` : ""}`;
    }
    const delay = Number(row.delay_mins);
    const timing = Number.isFinite(delay) ? (delay > 0 ? `${delay} min late` : delay < 0 ? `${Math.abs(delay)} min early` : "on time") : row.status_text || "latest update";
    let action = row.actual_utc || /landed|arrived|departed/i.test(rawStatus)
      ? verb
      : row.estimated_utc || /expected|delay/i.test(rawStatus)
      ? "expected"
      : rawStatus.toLowerCase() || "scheduled";
    if (timing === "on time" && /on time/i.test(action)) action = "on time";
    const timingText = timing === "on time" && action === "on time" ? "" : ` · ${timing}`;
    return `${row.flight_number || "Flight"} ${action}${timingText}${clock ? ` at ${clock}` : ""}`;
  };
  const flightDetail = detail.flights || {};
  const flightCards = [
    { title: "Arrivals today", detail: `${Number(flightDetail.arrivals?.cancelled || 0)} cancelled · ${Number(flightDetail.arrivals?.meaningful_delay_15m_plus || 0)} delayed 15m+`, status: airport.status.flights },
    { title: "Departures today", detail: `${Number(flightDetail.departures?.cancelled || 0)} cancelled · ${Number(flightDetail.departures?.meaningful_delay_15m_plus || 0)} delayed 15m+`, status: airport.status.flights },
    movementLine(flightDetail.latest_arrival, "landed") ? { title: "Latest arrival", detail: movementLine(flightDetail.latest_arrival, "landed"), status: flightDetail.latest_arrival?.status_label || airport.status.flights } : null,
    movementLine(flightDetail.latest_departure, "departed") ? { title: "Latest departure", detail: movementLine(flightDetail.latest_departure, "departed"), status: flightDetail.latest_departure?.status_label || airport.status.flights } : null,
  ].filter(Boolean);
  const roadCards = Array.isArray(detail.road?.corridors) && detail.road.corridors.length
    ? detail.road.corridors.slice(0, airport.code === "LHR" ? 5 : 3).map((corridor) => {
        const affected = (corridor.segments || [])
          .filter((segment) => Number(segment.delay_minutes || 0) > 0)
          .sort((a, b) => Number(b.delay_minutes || 0) - Number(a.delay_minutes || 0))
          .slice(0, 2)
          .map((segment) => `${segment.name} +${segment.delay_minutes}m`)
          .join(" · ");
        const corridorTiming = Number(corridor.delay_minutes || 0) > 0
          ? `+${corridor.delay_minutes} min vs usual`
          : "Normal journey time";
        return {
          title: corridor.name,
          detail: `${corridorTiming}${affected ? ` · affected: ${affected}` : ""}${formatClock(corridor.observed_at) ? ` · checked ${formatClock(corridor.observed_at)}` : ""}`,
          status: corridor.level || airport.status.road,
        };
      })
    : bestRoadSignals(airport);
  const railServiceText = (service, direction) => {
    if (!service) return null;
    const expected = direction === "arrival"
      ? service.arrival_expected_local || service.expected_time_local || service.std_local
      : service.departure_expected_local || service.expected_time_local || service.std_local;
    const serviceName = service.service_id || service.operator_name || "Service";
    const route = service.context_summary || [service.origin_name || service.origin_crs, service.destination_name || service.destination_crs].filter(Boolean).join(" → ");
    return `${direction === "arrival" ? "Latest arrival" : "Latest departure"}: ${serviceName}${expected ? ` ${expected}` : ""}${route ? ` · ${route}` : ""}`;
  };
  const railCards = Array.isArray(detail.rail?.observations) && detail.rail.observations.length
    ? detail.rail.observations.slice(0, 6).map((row) => {
        const services = [railServiceText(row.latest_arrival, "arrival"), railServiceText(row.latest_departure, "departure")].filter(Boolean).join(" · ");
        const summary = row.cancelled ? `${row.cancelled} cancelled` : row.delayed ? `${row.delayed} delayed` : `${row.service_count || 0} services checked`;
        return { title: row.label, detail: `${summary}${services ? ` · ${services}` : ""} · ${formatClock(row.observed_at) ? `checked ${formatClock(row.observed_at)}` : "observation time unavailable"}`, status: row.level || airport.status.rail };
      })
    : bestRailSignals(airport);
  const queueSignalCards = detail.queues?.availability === "unavailable"
    ? [{ title: "Live queue data unavailable", detail: "No live security or immigration wait feed is available for this airport.", status: "Checking" }]
    : queueSignals(airport);
  const queueStatus = detail.queues?.availability === "unavailable" ? "Checking" : airport.status.queues;
  const terminalPressure = terminalPressureCards(airport);
  const queueTerminals = terminalQueueCards(airport);
  const terminalSummary = terminalPressure.length
    ? terminalPressure
        .map((item) => (typeof item === "string" ? item : `${item.terminal || "Terminal"} ${item.label || item.level || "Checking"}`))
        .join(" • ")
    : readableTerminalText(airport, airport.statusReasons?.terminals);
  const hasQueueTerminalCards = queueTerminals.some((item) => typeof item !== "string");
  const cardStyle = { ...previewCardShell(isCompact), overflow: "hidden" };

  return (
    <article id={`${airport.key}-live-preview`} style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: isCompact ? 23 : 36, fontWeight: 950, lineHeight: 1.08 }}>{airport.name} ({airport.code})</div>
          <div style={{ marginTop: 5, fontSize: isCompact ? 11.5 : 12, lineHeight: 1.35, color: "#4f6790", fontWeight: 750 }}>
            Live intelligence preview • Latest prepared snapshot
          </div>
        </div>
        <div
          style={{
            padding: isCompact ? "6px 10px" : "8px 12px",
            borderRadius: 999,
            background: "rgba(34, 197, 94, 0.16)",
            color: "#166534",
            fontWeight: 800,
            fontSize: isCompact ? 10 : 12,
            textTransform: "uppercase",
          }}
        >
          Live
        </div>
      </div>

      <div
        style={{
          minWidth: 0,
          maxWidth: "100%",
          marginTop: isCompact ? 12 : 18,
          display: "grid",
          gridTemplateColumns: isCompact ? "1fr" : "0.7fr 0.8fr 1.3fr",
          gap: 12,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            ...previewCardShell(isCompact),
            minHeight: isCompact ? 68 : 82,
          }}
        >
          <div style={{ width: isCompact ? 36 : 42, height: isCompact ? 36 : 42, flex: isCompact ? "0 0 36px" : "0 0 42px", borderRadius: 999, background: "rgba(245,158,11,0.20)", display: "grid", placeItems: "center", fontSize: isCompact ? 18 : 22 }}>📊</div>
          <div>
            <div style={{ color: "#5d708f", fontSize: isCompact ? 11.5 : 12, fontWeight: 800 }}>Overall status</div>
            <div style={{ color: statusTone(airport.status.overall).fg, fontSize: isCompact ? 19 : 24, lineHeight: 1.15, fontWeight: 950 }}>
              {statusLabel(airport.status.overall)}
            </div>
          </div>
        </div>
        <div
          style={{
            ...previewCardShell(isCompact),
            minHeight: isCompact ? 68 : 82,
          }}
        >
          <div style={{ color: "#5d708f", fontSize: isCompact ? 11.5 : 12, fontWeight: 800 }}>Weather</div>
          <div style={{ marginTop: 4, fontSize: isCompact ? 17 : 20, lineHeight: 1.2, fontWeight: 900 }}>{airport.preview.weather}</div>
          <div style={{ marginTop: 4, color: "#5d708f", fontSize: isCompact ? 11 : 12, lineHeight: 1.35 }}>Included in leave-time checks</div>
        </div>
        <button
          type="button"
          onClick={() => onOpenPlanner?.(airport.key)}
          style={{
            border: "1px solid #dbe7ff",
            borderRadius: isCompact ? 14 : 18,
            padding: isCompact ? 12 : 16,
            background: "rgba(255,255,255,0.92)",
            color: "#08204a",
            textAlign: "left",
            cursor: "pointer",
            boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
          }}
          title={`Open ${airport.code} planner`}
        >
          <div style={{ color: "#5d708f", fontSize: isCompact ? 11.5 : 12, fontWeight: 800 }}>Next step</div>
          <div style={{ marginTop: 4, fontSize: isCompact ? 14 : 16, lineHeight: 1.35, fontWeight: 900 }}>
            {airport.nextStep || "Open planner for when-to-leave timing"}
          </div>
          <div style={{ marginTop: 8, color: "#1d64d8", fontSize: isCompact ? 11.5 : 12, fontWeight: 900 }}>
            Open {airport.code} planner →
          </div>
        </button>
      </div>

      <div
        style={{
          minWidth: 0,
          maxWidth: "100%",
          marginTop: isCompact ? 12 : 16,
          display: "grid",
          gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1.05fr) minmax(0, 0.95fr)",
          gap: isCompact ? 18 : 14,
          alignItems: "start",
        }}
      >
        <StatusSection
          title="Road corridors"
          subtitle="Major routes and worst visible pressure"
          icon="🚘"
          status={airport.status.road}
          items={roadCards}
          columns={isCompact ? 1 : 2}
        />

        <StatusSection
          title="Flights"
          subtitle="Disruption, flow and latest visible service"
          icon="✈️"
          status={airport.status.flights}
          items={flightCards}
          columns={isCompact ? 1 : 2}
        />

        <StatusSection
          title="Rail and Underground"
          subtitle="Airport stations, lines and route status"
          icon="🚆"
          status={airport.status.rail}
          items={railCards}
          columns={isCompact ? 1 : 2}
        />

        <StatusSection
          title="Weather"
          subtitle="Latest airport observation and operational flags"
          icon="☁️"
          status={airport.status.weather}
          items={[
            {
              title: detail.weather?.headline || airport.preview.weather || "Current conditions",
              detail: detail.weather?.current
                ? `${detail.weather.current.wind_mps != null ? `Wind ${Number(detail.weather.current.wind_mps).toFixed(1)} m/s` : "Wind unavailable"} · checked ${formatClock(detail.weather.observed_at) || "recently"}`
                : detail.weather?.reason || "Latest weather observation is updating",
              status: detail.weather?.level || airport.status.weather,
            },
          ]}
          columns={1}
        />

        <div
          style={previewSectionShell(airport.status.terminals, isCompact)}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap", ...previewSectionHeader(airport.status.terminals, isCompact) }}>
            <div>
              <div style={{ fontSize: isCompact ? 16 : 20, lineHeight: 1.25, fontWeight: 900 }}>Terminal pressure</div>
              <div style={{ marginTop: 3, color: "#5d708f", fontSize: isCompact ? 12 : 13, lineHeight: 1.4 }}>Flight delay and cancellation status by terminal</div>
            </div>
            <StatusPill status={airport.status.terminals} compact={isCompact} />
          </div>
          <div
            style={{
              marginTop: 10,
              display: "grid",
              gridTemplateColumns: isCompact ? "repeat(2, minmax(0, 1fr))" : "repeat(2, minmax(0, 1fr))",
              gap: 9,
            }}
          >
            {terminalPressure.map((item, index) =>
              typeof item === "string" ? (
                <TerminalMiniCard key={`${airport.key}-terminal-pressure-${index}-${item}`} text={item} />
              ) : (
                <TerminalPressureCard key={`${airport.key}-terminal-pressure-${index}-${item.terminal || "terminal"}`} terminal={item} />
              )
            )}
          </div>
          <div style={{ marginTop: 8, color: "#4f6790", fontSize: 11, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {terminalSummary}
          </div>
        </div>

        <div
          style={previewSectionShell(queueStatus, isCompact)}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap", ...previewSectionHeader(queueStatus, isCompact) }}>
            <div>
              <div style={{ fontSize: isCompact ? 16 : 20, lineHeight: 1.25, fontWeight: 900 }}>Queue snapshot</div>
              <div style={{ marginTop: 3, color: "#5d708f", fontSize: isCompact ? 12 : 13, lineHeight: 1.4 }}>Security and border waits by terminal</div>
            </div>
            <StatusPill status={queueStatus} compact={isCompact} />
          </div>
          <div
            style={{
              marginTop: 10,
              display: "grid",
              gridTemplateColumns: isCompact ? "repeat(2, minmax(0, 1fr))" : "repeat(2, minmax(0, 1fr))",
              gap: 9,
            }}
          >
            {hasQueueTerminalCards ? queueTerminals.map((item, index) =>
              typeof item === "string" ? (
                <MiniStatusCard key={`${airport.key}-queue-text-${index}-${item}`} item={parseSignalText(item, queueStatus)} fallbackStatus={queueStatus} />
              ) : (
                <TerminalQueueCard key={`${airport.key}-queue-terminal-${item.terminal}`} terminal={item} />
              )
            ) : queueSignalCards.slice(0, 4).map((item, index) => (
              <MiniStatusCard key={`${airport.key}-queue-signal-card-${index}`} item={item} fallbackStatus={queueStatus} />
            ))}
          </div>
          {queueSignalCards.length ? (
            <div style={{ marginTop: 10, display: "grid", gap: 6 }}>
              {queueSignalCards.slice(0, 2).map((item, index) => (
                <InlineSignal key={`${airport.key}-queue-signal-${index}`} item={item} status={airport.status.queues} />
              ))}
            </div>
          ) : null}
        </div>
      </div>

    </article>
  );
}

function StatusSection({ title, subtitle, icon, status, items, columns = 2 }) {
  const isCompact = useCompactMobile();
  return (
    <div
      style={previewSectionShell(status, isCompact)}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "start", minWidth: 0, ...previewSectionHeader(status, isCompact) }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, fontSize: isCompact ? 16 : 20, lineHeight: 1.25, fontWeight: 900 }}>
            <span style={{ width: isCompact ? 26 : 30, height: isCompact ? 26 : 30, flex: isCompact ? "0 0 26px" : "0 0 30px", borderRadius: 999, background: "#e8f1ff", display: "grid", placeItems: "center", fontSize: isCompact ? 14 : 16 }}>{icon}</span>
            <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{title}</span>
          </div>
          <div style={{ marginTop: 3, color: "#5d708f", fontSize: isCompact ? 12 : 13, lineHeight: 1.4, overflowWrap: "anywhere" }}>{subtitle}</div>
        </div>
        <StatusPill status={status} compact={isCompact} />
      </div>
      <div
        style={{
          minWidth: 0,
          maxWidth: "100%",
          marginTop: 10,
          display: "grid",
          gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
          gap: 9,
        }}
      >
        {(items?.length ? items : [{ title: "Status checking", detail: "Live checks updating", status }]).map((item, index) => (
          <MiniStatusCard key={`${title}-${index}-${item.title}`} item={item} fallbackStatus={status} />
        ))}
      </div>
    </div>
  );
}

function MiniStatusCard({ item, fallbackStatus }) {
  const surface = statusSurface(item.status || fallbackStatus);
  return (
    <div
      title={[item.title, item.detail].filter(Boolean).join(" • ")}
      style={{
        background: surface.bg,
        border: `1px solid ${surface.border}`,
        borderRadius: 10,
        padding: "9px 10px",
        minWidth: 0,
        boxShadow: "inset 3px 0 0 rgba(37, 99, 235, 0.12)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
        <div style={{ minWidth: 0, color: "#0b1f45", fontSize: 12, lineHeight: 1.3, fontWeight: 900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {item.title}
        </div>
        <StatusPill status={item.status || fallbackStatus} compact />
      </div>
      <div style={{ marginTop: 5, color: "#4f6790", fontSize: 11, lineHeight: 1.45, overflowWrap: "anywhere", wordBreak: "break-word" }}>
        {item.detail || "Live status available"}
      </div>
    </div>
  );
}

function InlineSignal({ item, status }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 8,
        alignItems: "center",
        borderTop: "1px solid #e6eefb",
        paddingTop: 6,
        fontSize: 11,
      }}
    >
      <span style={{ color: "#4f6790", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {item.title}{item.detail ? ` • ${item.detail}` : ""}
      </span>
      <StatusPill status={item.status || status} compact />
    </div>
  );
}

function TerminalMiniCard({ text }) {
  const isCompact = useCompactMobile();
  const clean = stripStatusText(text);
  const bulletParts = clean.split(" • ").map((part) => part.trim()).filter(Boolean);
  const terminalMatch = clean.match(/^(T\d+)\s+(.+)$/i);
  const label = bulletParts.length > 1
    ? bulletParts[0]
    : terminalMatch?.[1] || clean || "Terminal";
  const status = bulletParts.length > 1
    ? bulletParts.slice(1).join(" • ")
    : terminalMatch?.[2] || "Status checking";
  const surface = statusSurface(status);
  return (
    <div
      style={{
        background: surface.bg,
        border: `1px solid ${surface.border}`,
        borderRadius: 10,
        padding: 9,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <span style={{ width: 32, height: 32, borderRadius: 999, background: "#dbeafe", color: "#1e3a8a", display: "grid", placeItems: "center", fontWeight: 950 }}>
          {label}
        </span>
        <StatusPill status={status} compact={isCompact} />
      </div>
    </div>
  );
}

function TerminalPressureCard({ terminal }) {
  const isCompact = useCompactMobile();
  const status = terminal.label || terminal.level || "Checking";
  const surface = statusSurface(status);
  const flights = Number(terminal.flights || 0);
  const arrivals = Number(terminal.arrivals || 0);
  const departures = Number(terminal.departures || 0);
  const cancelled = Number(terminal.cancelled || 0);
  const delayed30 = Number(terminal.delayed_30 || 0);
  const delayed60 = Number(terminal.delayed_60 || 0);
  const pressureLine = `${flights} flights • ${arrivals} arr • ${departures} dep`;
  const disruptionLine = `${cancelled} cancel • ${delayed30} delayed 30m+${delayed60 ? ` • ${delayed60} 60m+` : ""}`;
  return (
    <div
      title={`${terminal.terminal || "Terminal"} • ${pressureLine} • ${disruptionLine}`}
      style={{
        background: surface.bg,
        border: `1px solid ${surface.border}`,
        borderRadius: 10,
        padding: 9,
        display: "grid",
        gap: 6,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <span style={{ width: 32, height: 32, borderRadius: 999, background: "#dbeafe", color: "#1e3a8a", display: "grid", placeItems: "center", fontWeight: 950 }}>
          {terminal.terminal || "T"}
        </span>
        <StatusPill status={status} compact={isCompact} />
      </div>
      <div style={{ color: "#274060", fontSize: 10.5, lineHeight: 1.25, fontWeight: 750 }}>{pressureLine}</div>
      <div style={{ color: cancelled || delayed30 ? statusTone(status).fg : "#4f6790", fontSize: 10.5, lineHeight: 1.25, fontWeight: 850 }}>
        {disruptionLine}
      </div>
    </div>
  );
}

function TerminalQueueCard({ terminal }) {
  const isCompact = useCompactMobile();
  const lines = Array.isArray(terminal.lines) ? terminal.lines.filter(Boolean).slice(0, 3) : [];
  const surface = statusSurface(terminal.label || terminal.level);
  return (
    <div
      title={lines.map((line) => `${line.label} ${line.wait}`).join(" • ")}
      style={{
        background: surface.bg,
        border: `1px solid ${surface.border}`,
        borderRadius: 10,
        padding: 9,
        display: "grid",
        gap: 7,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={{ width: 32, height: 32, borderRadius: 999, background: "#dbeafe", color: "#1e3a8a", display: "grid", placeItems: "center", fontWeight: 950 }}>
          {terminal.terminal}
        </span>
        <StatusPill status={terminal.label || "Checking"} compact={isCompact} />
      </div>
      <div style={{ display: "grid", gap: 4 }}>
        {lines.length ? (
          lines.map((line) => (
            <div key={`${terminal.terminal}-${line.label}`} style={{ display: "flex", justifyContent: "space-between", gap: 6, color: "#274060", fontSize: 11, lineHeight: 1.2 }}>
              <span style={{ color: "#4f6790" }}>{line.label}</span>
              <strong style={{ color: statusTone(line.level).fg }}>{line.wait}</strong>
            </div>
          ))
        ) : (
          <div style={{ color: "#4f6790", fontSize: 11 }}>Queues checking</div>
        )}
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  minHeight: 44,
  borderRadius: 12,
  border: "1px solid #cddcf5",
  background: "#f8fbff",
  padding: "10px 12px",
  fontSize: 14,
  color: "#0f172a",
  outline: "none",
};

const buttonStyle = {
  width: "100%",
  minHeight: 44,
  borderRadius: 12,
  border: "none",
  color: "#ffffff",
  fontSize: 14,
  fontWeight: 800,
  cursor: "pointer",
};

export function AirportStatusOverview({
  title = "Live airport status",
  subtitle = "Live road, rail, flight, terminal, queue and weather checks from Airport Journey Planner.",
  showDetailedPreviews = true,
  maxWidth = 1320,
  hideDetailedOnMobile = false,
}) {
  const isCompact = useCompactMobile();
  const [liveAirports, setLiveAirports] = useState(airports);
  const airportList = useMemo(() => [liveAirports.heathrow, liveAirports.ema], [liveAirports]);

  useEffect(() => {
    let cancelled = false;

    const loadAirportIntelligence = async () => {
      try {
        const res = await api.get("/api/open/airport-intelligence-summary?airports=LHR,EMA");
        if (cancelled) return;
        const next = { ...airports };
        for (const airport of res?.data?.airports || []) {
          const key = airportKeyByCode[String(airport?.code || "").toUpperCase()];
          if (!key) continue;
          next[key] = normalizeLiveAirport(airports[key], airport);
        }
        setLiveAirports(next);
      } catch (_err) {
        if (!cancelled) setLiveAirports(airports);
      }
    };

    let timer = null;
    const schedulePolling = () => {
      if (timer) clearInterval(timer);
      timer = document.hidden ? null : setInterval(loadAirportIntelligence, 5 * 60 * 1000);
    };
    const handleVisibilityChange = () => {
      if (!document.hidden) loadAirportIntelligence();
      schedulePolling();
    };
    loadAirportIntelligence();
    schedulePolling();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const openPreview = (airportKey) => {
    const el = typeof document !== "undefined" ? document.getElementById(`${airportKey}-live-preview`) : null;
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openPlanner = (airportKey) => {
    const airport = airports[airportKey];
    if (airport?.path && typeof window !== "undefined") window.open(airport.path, "_blank", "noopener,noreferrer");
  };

  return (
    <section style={{ padding: isCompact ? "18px 14px 12px" : "26px 16px 16px" }}>
      <div style={{ maxWidth, margin: "0 auto", display: "grid", gap: isCompact ? 14 : 18 }}>
        <div>
          <div style={{ color: "#1d4ed8", fontSize: 13, fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.03em" }}>
            Airport Journey Planner live status
          </div>
          <h2 style={{ margin: "8px 0 6px", fontSize: isCompact ? 24 : 32, color: "#12397c", lineHeight: 1.12 }}>
            {title}
          </h2>
          <p style={{ margin: 0, maxWidth: 760, fontSize: isCompact ? 14 : 16, lineHeight: 1.55, color: "#51698f" }}>
            {subtitle}
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
            gap: 12,
          }}
        >
          {airportList.map((airport) => (
            <AirportStatusCard key={airport.key} airport={airport} onOpen={openPreview} />
          ))}
        </div>

        {showDetailedPreviews && !(hideDetailedOnMobile && isCompact)
          ? airportList.map((airport) => (
              <PreviewPanel key={`${airport.key}-minibusgrab-preview`} airport={airport} onOpenPlanner={openPlanner} />
            ))
          : null}
      </div>
    </section>
  );
}

export default function Homenewajpv2({ pageConfig = null }) {
  const isCompact = useCompactMobile();
  const isNarrow = useCompactMobile(430);
  const lockedAirportKey = pageConfig?.airportKey && airports[pageConfig.airportKey] ? pageConfig.airportKey : "";
  const isAirportLanding = Boolean(lockedAirportKey);
  const isRouteLanding = pageConfig?.type === "ajp-route-planner";
  const initialDirection = pageConfig?.direction === "return" ? "return" : "outbound";
  const initialAirportKey = lockedAirportKey || "heathrow";
  const [liveAirports, setLiveAirports] = useState(airports);
  const [selectedAirportKey, setSelectedAirportKey] = useState(initialAirportKey);
  const [planResponses, setPlanResponses] = useState({});
  const [planLoadingKey, setPlanLoadingKey] = useState("");
  const [planErrors, setPlanErrors] = useState({});
  const planStartedRef = useRef(false);
  const [forms, setForms] = useState({
    heathrow: { direction: lockedAirportKey === "heathrow" ? initialDirection : "outbound", terminal: "", postcode: "", flight: "", date: "", flight_anchor_utc: "" },
    gatwick: { direction: lockedAirportKey === "gatwick" ? initialDirection : "outbound", terminal: "", postcode: "", flight: "", date: "", flight_anchor_utc: "" },
    luton: { direction: lockedAirportKey === "luton" ? initialDirection : "outbound", terminal: "", postcode: "", flight: "", date: "", flight_anchor_utc: "" },
    stansted: { direction: lockedAirportKey === "stansted" ? initialDirection : "outbound", terminal: "", postcode: "", flight: "", date: "", flight_anchor_utc: "" },
    manchester: { direction: lockedAirportKey === "manchester" ? initialDirection : "outbound", terminal: "", postcode: "", flight: "", date: "", flight_anchor_utc: "" },
    ema: { direction: lockedAirportKey === "ema" ? initialDirection : "outbound", terminal: "", postcode: "", flight: "", date: "", flight_anchor_utc: "" },
  });

  useEffect(() => {
    const expectedCode = lockedAirportKey ? airports[lockedAirportKey]?.code : null;
    const saved = readAjpPlanSession(expectedCode);
    if (!saved?.journeyId || !saved?.airportCode) return;
    if (isRouteLanding && saved.path !== pageConfig?.path) return;
    const airportKey = airportKeyByCode[saved.airportCode];
    if (!airportKey || (lockedAirportKey && airportKey !== lockedAirportKey)) return;
    setForms((current) => ({ ...current, [airportKey]: { ...current[airportKey], ...(saved.form || {}), ...(pageConfig?.lockedDirection ? { direction: pageConfig.lockedDirection } : {}) } }));
    if (saved.planResponse?.success && saved.planResponse?.data) {
      setPlanResponses((current) => ({ ...current, [airportKey]: saved.planResponse }));
      setSelectedAirportKey(airportKey);
      trackFunnelEvent("ajp_plan_restored", { brand: "planner", tracking_source: "ajp_home", airport: saved.airportCode, ajp_journey_id: saved.journeyId, restore_source: "browser_session" });
    }
    let cancelled = false;
    api.get(`/api/ajp/v2/session/${saved.journeyId}`).then((response) => {
      if (cancelled || !response?.data?.success) return;
      setForms((current) => ({ ...current, [airportKey]: { ...current[airportKey], ...(saved.form || {}), ...(pageConfig?.lockedDirection ? { direction: pageConfig.lockedDirection } : {}) } }));
      setPlanResponses((current) => ({ ...current, [airportKey]: restoredPlanPayload(response.data.data) }));
      setSelectedAirportKey(airportKey);
      if (!saved.planResponse?.success) trackFunnelEvent("ajp_plan_restored", { brand: "planner", tracking_source: "ajp_home", airport: saved.airportCode, ajp_journey_id: saved.journeyId, restore_source: "backend_session" });
    }).catch(() => { /* An expired or different-browser session is intentionally ignored. */ });
    return () => { cancelled = true; };
  }, [isRouteLanding, lockedAirportKey, pageConfig?.lockedDirection, pageConfig?.path]);

  useEffect(() => {
    trackFunnelEventOnce("landing_view", {
      brand: "planner",
      tracking_source: pageConfig?.trackingSource || (isAirportLanding ? "ajp_paid_airport" : "ajp_home"),
      page_type: isRouteLanding ? "route_planner" : isAirportLanding ? "paid_airport_planner" : "home",
      route_key: pageConfig?.key,
      airport: lockedAirportKey ? airports[lockedAirportKey]?.code : undefined,
    });
  }, [isAirportLanding, isRouteLanding, lockedAirportKey, pageConfig?.key, pageConfig?.trackingSource]);

  useEffect(() => {
    if (lockedAirportKey) setSelectedAirportKey(lockedAirportKey);
  }, [lockedAirportKey]);

  useEffect(() => {
    let cancelled = false;

    const loadAirportIntelligence = async () => {
      try {
        const requestedCodes = lockedAirportKey ? airports[lockedAirportKey].code : "LHR,LGW,LTN,STN,MAN,EMA";
        const res = await api.get(`/api/open/airport-intelligence-summary?airports=${requestedCodes}`);
        if (cancelled) return;
        const next = { ...airports };
        for (const airport of res?.data?.airports || []) {
          const key = airportKeyByCode[String(airport?.code || "").toUpperCase()];
          if (!key) continue;
          next[key] = normalizeLiveAirport(airports[key], airport);
        }
        setLiveAirports(next);
      } catch (_err) {
        if (!cancelled) setLiveAirports(airports);
      }
    };

    let timer = null;
    const schedulePolling = () => {
      if (timer) clearInterval(timer);
      timer = document.hidden ? null : setInterval(loadAirportIntelligence, 5 * 60 * 1000);
    };
    const handleVisibilityChange = () => {
      if (!document.hidden) loadAirportIntelligence();
      schedulePolling();
    };
    loadAirportIntelligence();
    schedulePolling();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [lockedAirportKey]);

  const airportList = useMemo(
    () => lockedAirportKey
      ? [liveAirports[lockedAirportKey]]
      : [liveAirports.heathrow, liveAirports.gatwick, liveAirports.luton, liveAirports.stansted, liveAirports.manchester, liveAirports.ema],
    [liveAirports, lockedAirportKey]
  );

  const updateField = (airportKey, field, value) => {
    if (!planStartedRef.current) {
      planStartedRef.current = true;
      trackFunnelEvent("ajp_plan_start", {
        brand: "planner",
        tracking_source: "ajp_home",
        airport: airports[airportKey]?.code,
        field_name: field,
      });
    }
    setForms((prev) => ({
      ...prev,
      [airportKey]: {
        ...prev[airportKey],
        [field]: value,
      },
    }));
    setPlanErrors((prev) => ({ ...prev, [airportKey]: "" }));
    setPlanResponses((prev) => ({ ...prev, [airportKey]: null }));
  };

  const plannerUrl = (airportKey, response = null, viewMode = "") => {
    const airport = airports[airportKey];
    const form = forms[airportKey];
    const params = new URLSearchParams();
    const postcode = String(form.postcode || "").trim();
    const flight = String(form.flight || "").trim().toUpperCase();

    if (postcode) params.set(form.direction === "return" ? "destination_postcode" : "pickup_postcode", postcode);
    if (flight) params.set("flight_number", flight);
    if (form.date) params.set("flight_date", form.date);
    if (form.flight_anchor_utc) params.set("flight_anchor_utc", form.flight_anchor_utc);
    if (form.terminal) params.set("terminal", form.terminal);
    if (form.direction === "return") {
      params.set("direction", "return");
      if (form.flight_anchor_utc) params.set("arrival_time_utc", form.flight_anchor_utc);
    }
    if (viewMode) params.set("view_mode", viewMode);
    const token = response?.journey?.token;
    if (token) params.set("token", token);

    return params.toString() ? `${airport.path}?${params.toString()}` : airport.path;
  };

  const openDetailedPlan = (airportKey, viewMode = "") => {
    if (typeof window !== "undefined") window.open(plannerUrl(airportKey, planResponses[airportKey], viewMode), "_blank", "noopener,noreferrer");
  };

  const submitPlanner = async (airportKey) => {
    if (!['heathrow', 'ema', 'manchester', 'stansted', 'gatwick', 'luton'].includes(airportKey)) {
      if (typeof window !== "undefined") window.open(plannerUrl(airportKey), "_blank", "noopener,noreferrer");
      return;
    }
    const form = forms[airportKey] || {};
    const postcode = String(form.postcode || "").trim();
    const hasResolvableLocation = Boolean(postcode || form.place_id || String(form.pickup_address || "").trim());
    const flight = String(form.flight || "").replace(/\s+/g, "").toUpperCase();
    const flightAnchorUtc = form.flight_anchor_utc || londonLocalToUtcIso(form.manual_local);
    const flightDate = form.date || String(form.manual_local || "").slice(0, 10);
    const isReturn = form.direction === "return";
    const eventContext = {
      brand: "planner",
      tracking_source: pageConfig?.trackingSource || "ajp_home",
      page_type: isRouteLanding ? "route_planner" : isAirportLanding ? "paid_airport_planner" : "home",
      route_key: pageConfig?.key,
      airport: airports[airportKey]?.code,
    };
    const requestStartedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
    trackFunnelEvent("ajp_plan_submit", {
      ...eventContext,
      has_postcode: Boolean(postcode),
      has_flight: Boolean(flight),
      has_departure_time: !isReturn && Boolean(flightAnchorUtc),
      has_arrival_time: isReturn && Boolean(flightAnchorUtc),
      direction: isReturn ? "from_airport" : "to_airport",
    });
    if (!hasResolvableLocation || !flightAnchorUtc) {
      setPlanErrors((prev) => ({ ...prev, [airportKey]: `Add your ${isReturn ? "destination" : "pickup"} address and exact flight ${isReturn ? "arrival" : "departure"} time. The flight number is optional.` }));
      trackFunnelEvent("ajp_plan_failed", { ...eventContext, failure_reason: "missing_required_fields" });
      trackFunnelEvent("ajp_validation_error", {
        ...eventContext, direction: isReturn ? "from_airport" : "to_airport",
        error_code: !hasResolvableLocation ? "location_required" : "flight_time_required",
        error_stage: !hasResolvableLocation ? "location_validation" : "time_validation",
      });
      return;
    }
    if (new Date(flightAnchorUtc).getTime() <= Date.now()) {
      setPlanErrors((prev) => ({ ...prev, [airportKey]: `Choose a future flight ${isReturn ? "arrival" : "departure"} date and time.` }));
      trackFunnelEvent("ajp_validation_error", {
        ...eventContext,
        direction: isReturn ? "from_airport" : "to_airport",
        error_code: "flight_time_in_past",
        error_stage: "time_validation",
      });
      return;
    }
    setPlanLoadingKey(airportKey);
    setPlanErrors((prev) => ({ ...prev, [airportKey]: "" }));
    try {
      const endpoint = isReturn ? "/api/ajp/v2/return-plan" : "/api/ajp/v2/plan";
      const requestPayload = isReturn ? {
        visitor_id: getFunnelJourneyId(),
        destination_postcode: postcode.replace(/\s+/g, ""),
        destination_address: form.pickup_address || undefined,
        destination_place_id: form.place_id || undefined,
        airport_code: airports[airportKey]?.code,
        terminal: form.terminal || undefined,
        ...(flight ? { flight_number: flight } : {}),
        ...(flightDate ? { flight_date: flightDate } : {}),
        arrival_time_utc: flightAnchorUtc,
        manual_arrival_time: Boolean(form.manual_local),
        passengers: 4,
        adults: 2,
        children: 2,
        bags: 2,
        preferred_modes: ["taxi", "rail", "self_drive"],
        include_fares: true,
        include_google: false,
        road_provider: "tomtom",
        days_parked: 4,
      } : {
        visitor_id: getFunnelJourneyId(),
        postcode: postcode.replace(/\s+/g, ""),
        pickup_address: form.pickup_address || undefined,
        pickup_place_id: form.place_id || undefined,
        airport_code: airports[airportKey]?.code,
        terminal: form.terminal || undefined,
        flight_number: flight,
        flight_date: flightDate,
        flight_anchor_utc: flightAnchorUtc,
        manual_flight_time: Boolean(form.manual_local),
        direction: "Departure",
        passengers: 4,
        adults: 2,
        children: 2,
        bags: 0,
        preferred_modes: ["taxi", "rail", "self_drive"],
        include_rail_plan: true,
        include_google: false,
        road_provider: "tomtom",
        days_stay: 4,
        buffer_mode: "standard",
      };
      const response = await api.post(endpoint, requestPayload);
      const payload = response?.data || null;
      if (!payload?.success || !payload?.data) throw new Error(payload?.error || "The journey engine did not return a complete plan.");
      setPlanResponses((prev) => ({ ...prev, [airportKey]: payload }));
      saveAjpPlanSession({ journeyId: payload?.data?.journey_id, airportCode: airports[airportKey]?.code, form: forms[airportKey], planResponse: payload });
      trackFunnelEvent("ajp_plan_created", {
        ...eventContext,
        journey_id: payload?.data?.journey_id,
        ajp_journey_id: payload?.data?.journey_id,
        flight_number: flight,
        direction: isReturn ? "from_airport" : "to_airport",
        request_duration_ms: Math.round((typeof performance !== "undefined" ? performance.now() : Date.now()) - requestStartedAt),
      });
      trackAjpPlanGoogleAdsConversion(payload?.data?.journey_id);
      ["rail", "taxi", "self_drive"].forEach((mode) => {
        const cards = payload?.data?.ui_projection?.cards || payload?.ui_projection?.cards || {};
        const card = cards?.[mode] || (mode === "rail" ? cards?.train : null) || {};
        const railState = mode === "rail" ? card?.feasibility?.state : null;
        trackFunnelEvent("ajp_transport_result_view", {
          ...eventContext,
          mode,
          status: railState && railState !== "viable" ? "not_viable" : card.status,
          reason: mode === "rail" ? card?.feasibility?.reason_code || card?.reason_code : undefined,
          terminal_arrival_at: mode === "rail" ? card?.feasibility?.evidence?.terminal_arrival_at || card?.terminal_arrival_at : undefined,
          terminal_lateness_mins: mode === "rail" ? card?.feasibility?.evidence?.terminal_lateness_mins : undefined,
          fare_gbp: card.cost_gbp ?? card.fare_gbp,
          duration_mins: card.duration_mins,
          direction: isReturn ? "from_airport" : "to_airport",
          ajp_journey_id: payload?.data?.journey_id,
        });
      });
      if (typeof document !== "undefined") {
        requestAnimationFrame(() => document.querySelector('[aria-label="Journey plan preview"]')?.scrollIntoView({ behavior: "smooth", block: "start" }));
      }
    } catch (error) {
      const classified = classifyAjpFailure(error);
      setPlanErrors((prev) => ({ ...prev, [airportKey]: error?.response?.data?.error || error?.message || "We could not build this plan right now. Please try again." }));
      trackFunnelEvent("ajp_plan_failed", {
        ...eventContext,
        failure_reason: classified.error_code,
        ...classified,
        direction: isReturn ? "from_airport" : "to_airport",
        request_duration_ms: Math.round((typeof performance !== "undefined" ? performance.now() : Date.now()) - requestStartedAt),
      });
      trackFunnelEvent(classified.error_code === "location_resolution_failed" ? "ajp_location_resolution_failed" : classified.error_stage?.includes("validation") ? "ajp_validation_error" : "ajp_api_error", { ...eventContext, ...classified, direction: isReturn ? "from_airport" : "to_airport", flight_number: flight || undefined });
    } finally {
      setPlanLoadingKey("");
    }
  };

  const openPreview = (airportKey) => {
    const el = typeof document !== "undefined" ? document.getElementById(`${airportKey}-live-preview`) : null;
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const selectAirportTerminal = (airportKey, terminal) => {
    const nextAirportKey = lockedAirportKey || airportKey;
    const airportChanged = nextAirportKey !== selectedAirportKey;
    setSelectedAirportKey(nextAirportKey);
    setForms((prev) => ({
      ...prev,
      [nextAirportKey]: airportChanged
        ? {
            ...prev[nextAirportKey],
            direction: prev[selectedAirportKey]?.direction || prev[nextAirportKey]?.direction || "outbound",
            postcode: prev[selectedAirportKey]?.postcode || "",
            pickup_address: prev[selectedAirportKey]?.pickup_address || "",
            place_id: prev[selectedAirportKey]?.place_id || "",
            latitude: prev[selectedAirportKey]?.latitude || "",
            longitude: prev[selectedAirportKey]?.longitude || "",
            terminal,
            flight: "",
            date: "",
            flight_anchor_utc: "",
            manual_local: "",
          }
        : { ...prev[nextAirportKey], terminal },
    }));
    setPlanErrors((prev) => ({ ...prev, [nextAirportKey]: "" }));
    setPlanResponses((prev) => ({ ...prev, [nextAirportKey]: null }));
  };

  const pageTitle = pageConfig?.title || "Airport Journey Planner | Compare Rail, Taxi & Driving Live";
  const pageDescription = pageConfig?.description || "Plan journeys to Heathrow, Gatwick, Luton, Stansted, Manchester and East Midlands airports. Compare rail, taxi and driving with live traffic, flight, weather and terminal intelligence.";
  const pageCanonical = pageConfig?.canonicalUrl || "https://www.airportjourneyplanner.com/";
  const routeStructuredData = isRouteLanding ? {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${pageCanonical}#webpage`,
        url: pageCanonical,
        name: pageTitle,
        description: pageDescription,
        inLanguage: "en-GB",
        isPartOf: { "@id": "https://www.airportjourneyplanner.com/#website" },
        about: { "@type": "Airport", name: pageConfig.airportName, iataCode: pageConfig.airportCode },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Airport Journey Planner", item: "https://www.airportjourneyplanner.com/" },
          { "@type": "ListItem", position: 2, name: pageConfig.airportName, item: `https://www.airportjourneyplanner.com/plan/${pageConfig.airportKey === "heathrow" ? "heathrow-airport" : pageConfig.airportKey}` },
          { "@type": "ListItem", position: 3, name: pageConfig.heading, item: pageCanonical },
        ],
      },
      {
        "@type": "FAQPage",
        mainEntity: (pageConfig.faqs || []).map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: { "@type": "Answer", text: faq.answer },
        })),
      },
      {
        "@type": ["SoftwareApplication", "WebApplication"],
        "@id": "https://www.airportjourneyplanner.com/#app",
        name: "Airport Journey Planner",
        applicationCategory: "TravelApplication",
        operatingSystem: "All",
        offers: { "@type": "Offer", price: "0", priceCurrency: "GBP" },
        featureList: [
          "Flight-aware leave-home and airport-exit time calculation",
          "Flight number, schedule and terminal checks",
          "Rail departure time, arrival time, duration, changes and estimated fare",
          "Taxi journey time, route and estimated fare",
          "Minibus journey planning and tailored group quote enquiries",
          "Self-drive journey time, airport parking and fuel estimates",
          "Journey alerts for monitored condition changes",
          "Airport weather, disruption and operational status",
          "Terminal, security and immigration queue information where available",
        ],
      },
    ],
  } : null;
  const routeSchemaJson = routeStructuredData ? JSON.stringify(routeStructuredData) : "";

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.title = pageTitle;
    const ensureHeadElement = (selector, tagName, attributes = {}) => {
      let element = document.head.querySelector(selector);
      if (!element) {
        element = document.createElement(tagName);
        Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
        element.setAttribute("data-ajp-configured-page", "true");
        document.head.appendChild(element);
      }
      return element;
    };
    ensureHeadElement('meta[name="description"]', "meta", { name: "description" }).setAttribute("content", pageDescription);
    ensureHeadElement('meta[name="robots"]', "meta", { name: "robots" }).setAttribute("content", pageConfig?.robots || "index,follow");
    ensureHeadElement('link[rel="canonical"]', "link", { rel: "canonical" }).setAttribute("href", pageCanonical);
    ensureHeadElement('meta[property="og:title"]', "meta", { property: "og:title" }).setAttribute("content", pageTitle);
    ensureHeadElement('meta[property="og:description"]', "meta", { property: "og:description" }).setAttribute("content", pageDescription);
    ensureHeadElement('meta[property="og:url"]', "meta", { property: "og:url" }).setAttribute("content", pageCanonical);
    ensureHeadElement('meta[name="twitter:title"]', "meta", { name: "twitter:title" }).setAttribute("content", pageTitle);
    ensureHeadElement('meta[name="twitter:description"]', "meta", { name: "twitter:description" }).setAttribute("content", pageDescription);
    if (routeSchemaJson) {
      const script = ensureHeadElement('script[data-ajp-route-schema="true"]', "script", { type: "application/ld+json", "data-ajp-route-schema": "true" });
      script.textContent = routeSchemaJson;
    } else {
      document.head.querySelector('script[data-ajp-route-schema="true"]')?.remove();
    }
  }, [pageCanonical, pageConfig, pageDescription, pageTitle, routeSchemaJson]);

  return (
    <div style={{ background: "linear-gradient(180deg, #f7fbff 0%, #eef5ff 42%, #f7fbff 100%)", WebkitTextSizeAdjust: "100%", textSizeAdjust: "100%" }}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta
          name="description"
          content={pageDescription}
        />
        <meta
          name="keywords"
          content="airport journey planner, when to leave for airport, airport taxi rail driving comparison, live airport status, Heathrow, Gatwick, Luton, Stansted, Manchester, East Midlands Airport"
        />
        <meta name="robots" content={pageConfig?.robots || "index,follow"} />
        <link rel="canonical" href={pageCanonical} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta
          property="og:description"
          content={pageDescription}
        />
        <meta property="og:url" content={pageCanonical} />
        <meta property="og:image" content="https://www.airportjourneyplanner.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta
          name="twitter:description"
          content={pageDescription}
        />
        <meta name="twitter:image" content="https://www.airportjourneyplanner.com/og-image.jpg" />
        {isRouteLanding ? (
          <script type="application/ld+json">{JSON.stringify(routeStructuredData)}</script>
        ) : (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "WebSite",
                "@id": "https://www.airportjourneyplanner.com/#website",
                name: "Airport Journey Planner",
                url: "https://www.airportjourneyplanner.com/",
                description:
                  "Live UK airport journey planning with flight, terminal, rail, road, weather and queue intelligence.",
                inLanguage: "en-GB",
              },
              {
                "@type": "Organization",
                "@id": "https://www.airportjourneyplanner.com/#organization",
                name: "Airport Journey Planner",
                url: "https://www.airportjourneyplanner.com/",
                logo: "https://www.airportjourneyplanner.com/logo.png",
                parentOrganization: {
                  "@type": "Organization",
                  name: "CheapCabGrab",
                  url: "https://www.cheapcabgrab.com/",
                },
              },
              {
                "@type": ["SoftwareApplication", "WebApplication"],
                "@id": "https://www.airportjourneyplanner.com/#app",
                name: "Airport Journey Planner",
                url: "https://www.airportjourneyplanner.com/",
                applicationCategory: "TravelApplication",
                operatingSystem: "All",
                description:
                  "A live journey planning tool covering six major UK airports.",
                offers: {
                  "@type": "Offer",
                  price: "0",
                  priceCurrency: "GBP",
                },
                featureList: [
                  "Live airport status summaries",
                  "Flight-aware journey planning",
                  "Road and rail intelligence",
                  "Weather and terminal queue signals",
                  "Rail, taxi and self-drive comparison",
                ],
              },
              {
                "@type": "ItemList",
                "@id": "https://www.airportjourneyplanner.com/#supported-airports",
                name: "Supported airports",
                itemListElement: [
                  {
                    "@type": "ListItem",
                    position: 1,
                    name: "Heathrow Airport",
                    url: "https://www.airportjourneyplanner.com/heathrow",
                  },
                  {
                    "@type": "ListItem",
                    position: 2,
                    name: "Gatwick Airport",
                    url: "https://www.airportjourneyplanner.com/train-vs-taxi-gatwick",
                  },
                  { "@type": "ListItem", position: 3, name: "Luton Airport", url: "https://www.airportjourneyplanner.com/train-vs-taxi-luton-airport" },
                  { "@type": "ListItem", position: 4, name: "Stansted Airport", url: "https://www.airportjourneyplanner.com/train-vs-taxi-stansted" },
                  { "@type": "ListItem", position: 5, name: "Manchester Airport", url: "https://www.airportjourneyplanner.com/train-vs-taxi-manchester-airport" },
                  {
                    "@type": "ListItem",
                    position: 6,
                    name: "East Midlands Airport",
                    url: "https://www.airportjourneyplanner.com/east-midlands-airport",
                  },
                ],
              },
            ],
          })}
        </script>
        )}
      </Helmet>
      <section
        style={{
          padding: isNarrow ? "10px 10px 14px" : isCompact ? "14px 14px 18px" : "30px 20px 32px",
          background:
            "linear-gradient(110deg, #eef5ff 0%, #e4effc 48%, #c7ddf4 100%)",
          borderBottom: "1px solid #dbe7ff",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            width: "100%",
            height: isNarrow ? 190 : isCompact ? 220 : 430,
            background:
              "url('/ajp-hero-premium.webp') right top / auto 100% no-repeat",
            filter: isCompact ? "contrast(1.02) saturate(.92)" : "contrast(1.08) saturate(1.02)",
            opacity: isCompact ? 0.22 : 0.98,
            WebkitMaskImage: "radial-gradient(ellipse 105% 112% at 100% 0%, #000 0%, #000 55%, rgba(0,0,0,.86) 66%, rgba(0,0,0,.38) 80%, transparent 100%)",
            maskImage: "radial-gradient(ellipse 105% 112% at 100% 0%, #000 0%, #000 55%, rgba(0,0,0,.86) 66%, rgba(0,0,0,.38) 80%, transparent 100%)",
            pointerEvents: "none",
          }}
        />

        <div style={{ maxWidth: 1440, margin: "0 auto", position: "relative", zIndex: 1 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1fr)",
              gap: isNarrow ? 10 : isCompact ? 16 : 32,
              alignItems: "start",
            }}
          >
            <div
              style={{
                position: "relative",
                minHeight: "auto",
                padding: isNarrow ? 0 : isCompact ? "2px 0 0" : "12px 0 0",
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: isNarrow ? "5px 8px" : isCompact ? "6px 10px" : "8px 13px",
                  borderRadius: 999,
                  background: "#dbeafe",
                  color: "#1d4ed8",
                  fontSize: isNarrow ? 9 : isCompact ? 10 : 12,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.03em",
                }}
              >
                <span style={{ color: "#16a34a" }}>●</span> {pageConfig?.badge || "Live airport journey planning"}
              </div>
              <h1
                style={{
                  margin: isNarrow ? "8px 0 6px" : isCompact ? "11px 0 8px" : "28px 0 18px",
                  fontSize: isNarrow ? 31 : isCompact ? 36 : 72,
                  lineHeight: isCompact ? 1.02 : 1,
                  color: "#071948",
                  letterSpacing: 0,
                  maxWidth: isCompact ? "100%" : 760,
                  fontWeight: 950,
                }}
              >
                {isAirportLanding ? pageConfig.heading : <>Your complete airport journey. <span style={{ color: "#075bea" }}>There and home again.</span></>}
              </h1>
              <p
                style={{
                  margin: 0,
                  maxWidth: isCompact ? "100%" : 690,
                  fontSize: isNarrow ? 12 : isCompact ? 14 : 20,
                  lineHeight: isNarrow ? 1.35 : isCompact ? 1.42 : 1.55,
                  color: "#5a6b87",
                }}
              >
                {isAirportLanding ? pageConfig.intro : isCompact ? "Compare rail, taxi, minibus and self-drive for a smoother, smarter airport journey." : "Compare rail, taxi, minibus and self-drive with live traffic, disruption, flight and terminal intelligence—whether you’re flying out or travelling home."}
              </p>

              <div
                style={{
                  marginTop: isNarrow ? 8 : isCompact ? 12 : 16,
                  display: "flex",
                  flexWrap: isCompact ? "nowrap" : "wrap",
                  gap: isNarrow ? 4 : isCompact ? 6 : 10,
                }}
              >
                {heroChips.map((chip) => (
                  <span
                    key={chip}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: isNarrow ? 3 : 7,
                      flex: isCompact ? "1 1 0" : "0 0 auto",
                      justifyContent: isCompact ? "center" : "flex-start",
                      padding: isNarrow ? "6px 3px" : isCompact ? "7px 5px" : "10px 14px",
                      borderRadius: 9,
                      background: "rgba(255,255,255,0.72)",
                      border: "1px solid #dbe7ff",
                      color: "#214c91",
                      fontWeight: 700,
                      fontSize: isNarrow ? 8 : isCompact ? 9 : 12,
                      lineHeight: 1.15,
                      textAlign: "center",
                      boxShadow: "0 8px 18px rgba(42, 82, 140, 0.05)",
                    }}
                  >
                    <span style={{ color: "#2563eb" }}>⌁</span>
                    {chip}
                  </span>
                ))}
              </div>

            </div>

            <div style={{ display: "none" }}>
              <div
                style={{
                  display: "grid",
                  gap: 10,
                  background: "transparent",
                  border: "none",
                  borderRadius: 20,
                  padding: 0,
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) auto",
                    alignItems: "center",
                    gap: 12,
                    color: "#071948",
                    fontWeight: 900,
                    fontSize: 18,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", minWidth: 0 }}>
                    <span>Live airport status</span>
                    <span style={{ color: "#16a34a", fontSize: 13 }}>●</span>
                    <span style={{ color: "#71829b", fontSize: 14, fontWeight: 700 }}>Latest prepared operational snapshot</span>
                  </div>
                  <a
                    href="/analytics-v2"
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      marginLeft: "auto",
                      color: "#245aa9",
                      fontWeight: 800,
                      cursor: "pointer",
                      padding: "2px 0",
                      fontSize: isCompact ? 13 : 14,
                      display: "inline-flex",
                      textDecoration: "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isCompact ? "View all →" : "View full analytics →"}
                  </a>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
                    gap: 12,
                  }}
                >
                  {airportList.map((airport) => (
                    <AirportStatusCard key={airport.key} airport={airport} onOpen={openPreview} />
                  ))}
                </div>
              </div>
            </div>
          </div>

          <HeroPlannerCard
            airportKey={selectedAirportKey}
            airportList={airportList}
            form={forms[selectedAirportKey]}
            onAirportChange={selectAirportTerminal}
            onChange={(field, value) => updateField(selectedAirportKey, field, value)}
            onSubmit={submitPlanner}
            loading={planLoadingKey === selectedAirportKey}
            error={planErrors[selectedAirportKey] || ""}
            lockedAirport={isAirportLanding}
            lockedDirection={pageConfig?.lockedDirection || ""}
            addressPlaceholder={pageConfig?.addressPlaceholder || ""}
            addressLabel={pageConfig?.addressLabel || ""}
          />

          <QuickJourneyPreview
            airport={liveAirports[selectedAirportKey]}
            form={forms[selectedAirportKey]}
            planResponse={planResponses[selectedAirportKey] || null}
            loading={planLoadingKey === selectedAirportKey}
            onOpenPlan={() => submitPlanner(selectedAirportKey)}
            onViewDetails={(viewMode) => openDetailedPlan(selectedAirportKey, viewMode)}
          />

          {!isAirportLanding ? (
            <AirportPulseStrip
              airportList={airportList}
              selectedKey={selectedAirportKey}
              onSelect={setSelectedAirportKey}
            />
          ) : null}

        </div>
      </section>

      <section style={{ padding: isCompact ? "14px 14px 8px" : "20px 16px 10px" }}>
        <div style={{ maxWidth: 1440, margin: "0 auto", display: "grid", gap: isCompact ? 14 : 18 }}>
          <PreviewPanel key={`${selectedAirportKey}-detail-preview`} airport={liveAirports[selectedAirportKey]} onOpenPlanner={openDetailedPlan} />
        </div>
      </section>

      {isRouteLanding ? <AjpRouteSeoContent page={pageConfig} compact={isCompact} /> : null}

      <section style={{ padding: isCompact ? "10px 14px 6px" : "14px 16px 10px" }}>
        <div
          style={{
            maxWidth: 1440,
            margin: "0 auto",
            background: "linear-gradient(180deg, rgba(255,255,255,0.94) 0%, rgba(248,251,255,0.9) 100%)",
            border: "1px solid rgba(206,220,243,0.95)",
            borderRadius: isCompact ? 18 : 28,
            boxShadow: "0 20px 48px rgba(15,23,42,0.1)",
            backdropFilter: "blur(14px)",
            display: "grid",
            gridTemplateColumns: isCompact ? "repeat(2, minmax(0, 1fr))" : "repeat(5, minmax(0, 1fr))",
            gap: 0,
            overflow: "hidden",
          }}
        >
          {heroMetrics.map((item, index) => (
            <div
              key={item.label}
              style={{
                padding: isCompact ? "16px 14px" : "24px 28px",
                borderLeft: !isCompact && index > 0 ? "1px solid rgba(213,224,244,0.95)" : "none",
                borderTop: isCompact && index >= 2 ? "1px solid rgba(213,224,244,0.95)" : "none",
                display: "grid",
                gap: 8,
                justifyItems: isCompact ? "start" : "center",
                alignContent: "center",
                minHeight: isCompact ? 104 : 132,
              }}
            >
              <div style={{ color: "#2563eb", fontSize: isCompact ? 18 : 28, fontWeight: 900, lineHeight: 1 }}>{item.icon}</div>
              <div style={{ color: "#0c2f72", fontSize: isCompact ? 20 : 26, fontWeight: 950, lineHeight: 1.02, letterSpacing: 0 }}>{item.value}</div>
              <div style={{ color: "#5c6f8d", fontSize: isCompact ? 12 : 13, fontWeight: 700, lineHeight: 1.25 }}>{item.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section style={{ padding: isCompact ? "6px 14px 10px" : "0 16px 14px" }}>
        <div
          style={{
            maxWidth: 1440,
            margin: "0 auto",
            background: "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(251,253,255,0.93) 100%)",
            border: "1px solid rgba(219,231,255,0.98)",
            borderRadius: isCompact ? 16 : 22,
            padding: isCompact ? "14px 14px" : "18px 22px",
            display: "grid",
            gridTemplateColumns: isCompact ? "1fr" : "auto 1fr",
            alignItems: "center",
            gap: isCompact ? 14 : 24,
            boxShadow: "0 14px 30px rgba(15,23,42,0.06)",
          }}
        >
          <div style={{ color: "#1e3a6d", fontSize: isCompact ? 14 : 16, fontWeight: 900, whiteSpace: isCompact ? "normal" : "nowrap" }}>
            Trusted data from leading sources
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact ? "repeat(2, minmax(0, 1fr))" : "repeat(6, minmax(0, 1fr))",
              gap: isCompact ? 10 : 12,
            }}
          >
            {sourceList.map((item, index) => (
              <div
                key={item.name}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: isCompact ? "flex-start" : "center",
                  gap: 8,
                  minHeight: isCompact ? 38 : 46,
                  padding: isCompact ? "6px 2px" : "6px 8px",
                  borderLeft: !isCompact && index > 0 ? "1px solid rgba(226,234,247,0.9)" : "none",
                  color: "#314363",
                  fontSize: isCompact ? 12 : 14,
                  fontWeight: 800,
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: isCompact ? 22 : 26,
                    height: isCompact ? 22 : 26,
                    borderRadius: 999,
                    background: "linear-gradient(180deg, #f4f8ff 0%, #e8f0ff 100%)",
                    color: "#245df0",
                    fontSize: isCompact ? 11 : 13,
                    fontWeight: 900,
                    boxShadow: "inset 0 0 0 1px rgba(183,204,241,0.8)",
                    flex: "0 0 auto",
                  }}
                >
                  {item.mark}
                </span>
                <span>{item.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section style={{ padding: isCompact ? "14px 14px 6px" : "18px 16px 8px" }}>
        <div style={{ maxWidth: 1440, margin: "0 auto" }}>
          <div style={{ marginBottom: isCompact ? 10 : 16 }}>
            <h2 style={{ margin: 0, fontSize: isCompact ? 24 : 30, color: "#12397c" }}>What the planner checks</h2>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact ? "repeat(2, minmax(0, 1fr))" : "repeat(auto-fit, minmax(200px, 1fr))",
              gap: isCompact ? 10 : 14,
            }}
          >
            {checkCards.map((card) => (
              <article
                key={card.title}
                style={{
                  background: "#ffffff",
                  border: "1px solid #dbe7ff",
                  borderRadius: isCompact ? 12 : 18,
                  padding: isCompact ? 12 : 18,
                  boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
                }}
              >
                <div style={{ fontSize: isCompact ? 20 : 28 }}>{card.icon}</div>
                <h3 style={{ margin: isCompact ? "8px 0 6px" : "12px 0 8px", fontSize: isCompact ? 14 : 18, color: "#12397c" }}>{card.title}</h3>
                <p style={{ margin: 0, fontSize: isCompact ? 12 : 14, lineHeight: 1.5, color: "#4b628d" }}>{card.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: isCompact ? "16px 14px 28px" : "22px 16px 38px" }}>
        <div
          style={{
            maxWidth: 1440,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1fr) minmax(300px, 0.9fr)",
            gap: isCompact ? 12 : 18,
          }}
        >
          <article
            style={{
              background: "#ffffff",
              border: "1px solid #dbe7ff",
              borderRadius: isCompact ? 14 : 22,
              padding: isCompact ? 16 : 22,
              boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
            }}
          >
            <h2 style={{ margin: 0, fontSize: isCompact ? 22 : 28, color: "#12397c" }}>How it helps</h2>
            <div style={{ marginTop: isCompact ? 12 : 18, display: "grid", gap: isCompact ? 12 : 16 }}>
              {helpSteps.map((step, idx) => (
                <div key={step.title} style={{ display: "grid", gridTemplateColumns: isCompact ? "28px minmax(0, 1fr)" : "36px minmax(0, 1fr)", gap: isCompact ? 10 : 14, alignItems: "start" }}>
                  <div
                    style={{
                      width: isCompact ? 28 : 36,
                      height: isCompact ? 28 : 36,
                      borderRadius: 999,
                      background: "#dbeafe",
                      color: "#1d4ed8",
                      display: "grid",
                      placeItems: "center",
                      fontWeight: 900,
                      fontSize: isCompact ? 12 : 16,
                    }}
                  >
                    {idx + 1}
                  </div>
                  <div>
                    <div style={{ fontSize: isCompact ? 14 : 18, fontWeight: 800, color: "#12397c" }}>{step.title}</div>
                    <div style={{ marginTop: 5, fontSize: isCompact ? 12 : 15, lineHeight: 1.55, color: "#51698f" }}>{step.body}</div>
                  </div>
                </div>
              ))}
            </div>
          </article>

          <article
            style={{
              background: "#ffffff",
              border: "1px solid #dbe7ff",
              borderRadius: isCompact ? 14 : 22,
              padding: isCompact ? 16 : 22,
              boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
            }}
          >
            <h2 style={{ margin: 0, fontSize: isCompact ? 22 : 28, color: "#12397c" }}>Coverage</h2>
            <div style={{ marginTop: 16, fontSize: 13, color: "#0f766e", fontWeight: 800 }}>Live now</div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
              {coverageLive.map((item) => (
                <span
                  key={item}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 999,
                    background: "#ecfdf5",
                    border: "1px solid #bbf7d0",
                    color: "#166534",
                    fontSize: 13,
                    fontWeight: 800,
                  }}
                >
                  {item}
                </span>
              ))}
            </div>
            <div style={{ marginTop: 18, fontSize: 13, color: "#7c3aed", fontWeight: 800 }}>Coming soon</div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
              {coverageSoon.map((item) => (
                <span
                  key={item}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 999,
                    background: "#f8fafc",
                    border: "1px solid #dbe2ea",
                    color: "#475569",
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  {item}
                </span>
              ))}
            </div>
            <p style={{ margin: "18px 0 0", fontSize: 14, lineHeight: 1.6, color: "#51698f" }}>
              More airports and deeper live modules can be added gradually without changing the overall homepage layout.
            </p>
          </article>
        </div>
      </section>
    </div>
  );
}
